/**
 * Scrudix API Client
 * Connects frontend to backend API
 */

import type {
  ExecutionProgressSnapshot,
  ExecutionPlan,
  ExecutionPlanInput,
  ExecutionTicketStatus,
  GoalPlan,
  GoalPlanInput,
  StoredExecutionPlan,
  StoredGoalPlan,
} from '../types';
import { readSSEEvents, streamTextDeltas } from './streaming';

const resolvedDefaultApiBase =
  typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:3001/api'
    : '/api';

const importMetaEnv = (import.meta as any).env || {};
const configuredApiBase = typeof importMetaEnv.VITE_API_URL === 'string'
  ? importMetaEnv.VITE_API_URL.trim().replace(/\/+$/, '')
  : '';

const API_BASE_URL = resolvedDefaultApiBase.startsWith('http://localhost')
  ? configuredApiBase || resolvedDefaultApiBase
  : '/api';

const STORAGE_KEYS = {
  token: 'scrudix_token',
  refreshToken: 'scrudix_refresh_token',
  auth: 'scrudix_auth',
  user: 'scrudix_user',
} as const;

function safeSetItem(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    console.warn(`[localStorage] Failed to write key "${key}":`, e);
    if (key === 'scrudix_user') {
      try {
        const parsed = JSON.parse(value);
        if (parsed.profilePicture) {
          delete parsed.profilePicture;
          safeSetItem(key, JSON.stringify(parsed));
        }
      } catch { /* ignore */ }
    }
  }
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export type EngineeringLoopWorkflow =
  | 'pr-risk-gate'
  | 'incident-commander'
  | 'migration-refactor'
  | 'issue-to-pr-autopilot'
  | 'guardian-2'
  | 'release-intelligence'
  | 'custom';
export type EngineeringLoopAutonomyMode = 'manual' | 'copilot' | 'autopilot';
export type EngineeringLoopRunStatus = 'pending' | 'running' | 'blocked' | 'completed' | 'failed' | 'cancelled';
export type EngineeringLoopStepStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | 'blocked' | 'skipped';
export type EngineeringLoopRiskLevel = 'low' | 'medium' | 'high' | 'critical';
export type EngineeringLoopActorType = 'system' | 'ai' | 'human';
export type EngineeringLoopEvidenceKind = 'diagnostic' | 'test' | 'build' | 'policy' | 'manual' | 'monitoring';
export type EngineeringLoopArtifactKind = 'file' | 'url' | 'log' | 'note';
export type EngineeringLoopTeamBrainType = 'incident_outcome' | 'decision' | 'coding_style' | 'failure_pattern';

export interface EngineeringLoopTrustEvidence {
  id?: string;
  kind: EngineeringLoopEvidenceKind;
  summary: string;
  passed?: boolean;
  details?: Record<string, unknown>;
  createdAt?: string;
}

export interface EngineeringLoopArtifact {
  id?: string;
  kind: EngineeringLoopArtifactKind;
  label: string;
  value: string;
}

export interface EngineeringLoopTrustMetadata {
  why: string;
  riskLevel: EngineeringLoopRiskLevel;
  riskSummary: string;
  validationPlan: string[];
  rollbackPlan: string[];
  requiresHumanApproval: boolean;
  evidence: EngineeringLoopTrustEvidence[];
}

export interface EngineeringLoopStep {
  id: string;
  phase: 'detect' | 'plan' | 'edit' | 'validate' | 'open_pr' | 'monitor';
  title: string;
  status: EngineeringLoopStepStatus;
  owner: 'ai' | 'human' | 'hybrid';
  notes?: string;
  startedAt?: string;
  completedAt?: string;
  trust: EngineeringLoopTrustMetadata;
  artifacts: EngineeringLoopArtifact[];
}

export interface EngineeringLoopTimelineEntry {
  id: string;
  event:
    | 'run_created'
    | 'step_updated'
    | 'approval_required'
    | 'status_changed'
    | 'team_brain_saved'
    | 'policy_exception';
  actor: EngineeringLoopActorType;
  summary: string;
  createdAt: string;
  stepId?: string;
  metadata?: Record<string, unknown>;
}

export interface EngineeringLoopRun {
  id: string;
  workflow: EngineeringLoopWorkflow;
  autonomyMode: EngineeringLoopAutonomyMode;
  status: EngineeringLoopRunStatus;
  title: string;
  objective: string;
  repository?: string;
  branch?: string;
  prUrl?: string;
  tags: string[];
  trustScore: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  steps: EngineeringLoopStep[];
  timeline: EngineeringLoopTimelineEntry[];
}

export interface EngineeringLoopRunSummary {
  id: string;
  workflow: EngineeringLoopWorkflow;
  autonomyMode: EngineeringLoopAutonomyMode;
  status: EngineeringLoopRunStatus;
  title: string;
  objective: string;
  repository?: string;
  branch?: string;
  trustScore: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  tags: string[];
  stepSummary: Record<EngineeringLoopStepStatus, number>;
}

export interface EngineeringLoopTeamBrainEntry {
  id: string;
  type: EngineeringLoopTeamBrainType;
  title: string;
  summary: string;
  signals: string[];
  sourceRunId?: string;
  confidence: number;
  tags: string[];
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface EngineeringLoopNextAction {
  type: 'resolve_blocker' | 'resume_run' | 'start_pr_risk_gate' | 'capture_memory';
  title: string;
  detail: string;
  confidence: number;
  runId?: string;
  workflow?: EngineeringLoopWorkflow;
}

export interface EngineeringLoopMetrics {
  windowDays: number;
  runsStarted: number;
  runsCompleted: number;
  completionRate: number;
  activeStreakDays: number;
  teamBrainTotal: number;
  autoCapturedMemories: number;
  approvalsRequired: number;
  coreWorkflowShare: number;
  nextAction: EngineeringLoopNextAction;
}

export interface EngineeringLoopGovernanceSummary {
  windowDays: number;
  generatedAt: string;
  runsEvaluated: number;
  blockedRuns: number;
  approvalRequiredEvents: number;
  policyExceptionEvents: number;
  highRiskRuns: number;
  averageTrustScore: number;
  autoCapturedMemories: number;
  pendingManualApprovals: number;
  topPolicyExceptions: Array<{
    runId: string;
    runTitle: string;
    createdAt: string;
    reason: string;
    approvedBy?: string;
  }>;
}

export interface UserProject {
  id: string;
  userId: string;
  name: string;
  tagline?: string;
  description?: string;
  logo?: string;
  tags?: string[];
  url?: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface CommunityPoll {
  question: string;
  options: Array<{ id: string; text: string; votes: number }>;
}

export interface CommunityUser {
  id: string;
  name: string;
  handle: string;
  avatar: string;
  headline: string;
  githubUsername?: string | null;
  followers: number;
  following: number;
  contributions?: number;
}

export interface CommunityProfile extends CommunityUser {
  bio: string | null;
  location: string | null;
  website: string | null;
  banner: string | null;
  skills: string[];
  joinedAt: string;
}

export interface CommunityPost {
  id: string;
  title?: string;
  content: string;
  image?: string;
  linkPreview?: Record<string, unknown>;
  poll?: CommunityPoll;
  fontFamily?: string;
  channel?: string;
  remixCode?: { filename?: string; code?: string; framework?: string };
  reactions?: Record<string, number>;
  createdAt: string;
  updatedAt: string;
  author: CommunityUser;
  likes: number;
  userVote: -1 | 0 | 1;
  commentsCount?: number;
  bookmarked?: boolean;
}

export interface CommunityComment {
  id: string;
  postId: string;
  parentId?: string;
  content: string;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
  isEdited: boolean;
  author: CommunityUser;
  likes: number;
  userVote: -1 | 0 | 1;
  replies: CommunityComment[];
}

export type TrustProjectStatus = 'discovery' | 'scoped' | 'active' | 'delivered' | 'paused';
export type TrustChangeRequestStatus = 'pending' | 'approved' | 'rejected';
export type TrustRiskLevel = 'low' | 'medium' | 'high';

export interface TrustAcceptanceCriterion {
  id: string;
  text: string;
  metric?: string;
}

export interface TrustSpecLock {
  title: string;
  objective: string;
  inScope: string[];
  outOfScope: string[];
  timelineDays?: number;
  budgetInr?: number;
  lockedAt: string;
}

export interface TrustAcceptanceContract {
  criteria: TrustAcceptanceCriterion[];
  createdAt: string;
}

export interface TrustChangeImpact {
  scope?: string;
  timelineDays?: number;
  costInr?: number;
}

export interface TrustChangeRequestEntry {
  id: string;
  summary: string;
  reason?: string;
  impact: TrustChangeImpact;
  status: TrustChangeRequestStatus;
  approvedBy?: string;
  requestedAt: string;
}

export interface TrustProject {
  id: string;
  name: string;
  founderBrief: string;
  status: TrustProjectStatus;
  createdAt: string;
  updatedAt: string;
  specLock?: TrustSpecLock;
  acceptanceContract?: TrustAcceptanceContract;
  changeLedger: TrustChangeRequestEntry[];
}

export interface TrustLayerState {
  projects: TrustProject[];
}

export interface TrustReport {
  projectId: string;
  projectName: string;
  status: TrustProjectStatus;
  hasSpecLock: boolean;
  acceptanceCriteriaCount: number;
  changes: {
    total: number;
    approved: number;
    pending: number;
    rejected: number;
  };
  alignmentScore: number;
  trustSignals: {
    scopeProtected: boolean;
    objectiveDefined: boolean;
    acceptanceDefined: boolean;
    unresolvedChanges: number;
  };
  updatedAt: string;
}

export interface TrustGhostSignal {
  id: string;
  pr: string;
  developer: string;
  risk: TrustRiskLevel;
  notes: string[];
}

export interface TrustForecastSignal {
  area: string;
  probability: number;
  window: string;
  impact: string;
  confidence: number;
}

export interface TrustActionMoment {
  id: string;
  when: string;
  title: string;
  detail: string;
  severity: TrustRiskLevel;
}

export interface TrustIntelligenceReport {
  relationshipHealth: number;
  previousWeekHealth: number;
  scopeChangesWithoutApproval: number;
  ghostSignals: TrustGhostSignal[];
  forecasts: TrustForecastSignal[];
  actionMoments: TrustActionMoment[];
}

export interface TrustScorecard {
  developerId: string;
  generatedAt: string;
  overallScore: number;
  dimensions: {
    deliveryReliability: number;
    codeQualitySignal: number;
    securityDiscipline: number;
    maintainability: number;
  };
  evidence: string[];
}

export interface TrustPostMortem {
  projectId: string;
  projectName: string;
  generatedAt: string;
  summary: string;
  timeline: string[];
  rootCause: string;
  estimatedSaveInr?: number;
  nextProjectRecommendation: string;
}

export interface TrustInvestorReport {
  projectId: string;
  projectName: string;
  generatedAt: string;
  verdict: string;
  codeQuality: {
    averageScore: number;
    criticalIssuesPrevented: number;
    criticalIssuesEscaped: number;
    securityPosture: 'good' | 'watch' | 'at_risk';
  };
  teamEfficiency: {
    acceptanceCoverage: number;
    changeApprovalRate: number;
    unresolvedChanges: number;
  };
  deliveryVelocity: {
    changesPerCycle: number;
    cycleHealth: number;
  };
  riskAssessment: {
    currentBlockers: number;
    estimatedDebtCostInr: number;
    topRisk: string;
  };
  highlights: string[];
}

export type EngineeringLoopSignalSource = 'github' | 'slack' | 'jira' | 'manual';
export type EngineeringLoopSignalStatus = 'new' | 'promoted' | 'resolved';

export interface EngineeringLoopCustomerSignal {
  id: string;
  source: EngineeringLoopSignalSource;
  title: string;
  detail: string;
  impact: EngineeringLoopRiskLevel;
  status: EngineeringLoopSignalStatus;
  createdAt: string;
  ingestedAt: string;
  externalId?: string;
  metadata?: Record<string, unknown>;
}

export interface EngineeringLoopSignalIngestionSummary {
  provider: EngineeringLoopSignalSource;
  ingested: number;
  skipped: number;
  status: 'completed' | 'partial' | 'skipped' | 'failed';
  message?: string;
}

export interface EngineeringLoopFailurePredictor {
  windowDays: number;
  score: number;
  severity: EngineeringLoopRiskLevel;
  forecast: string;
  drivers: string[];
  runsEvaluated: number;
  blockedRate: number;
  highRiskRate: number;
  completionGap: number;
  trustGap: number;
  approvalPressure: number;
}

export interface EngineeringLoopFailureAlertPolicy {
  enabled: boolean;
  thresholdScore: number;
  minSeverity: EngineeringLoopRiskLevel;
  cooldownMinutes: number;
  webhookUrl?: string;
  escalationTargets: string[];
  lastAlertAt?: string;
  lastAlertScore?: number;
  lastAlertSeverity?: EngineeringLoopRiskLevel;
}

export interface EngineeringLoopFailureAlert {
  id: string;
  status: 'fired' | 'suppressed';
  score: number;
  severity: EngineeringLoopRiskLevel;
  forecast: string;
  drivers: string[];
  thresholdScore: number;
  triggeredAt: string;
  cooldownUntil?: string;
  reason?: string;
  escalationStage: 'observe' | 'notify' | 'escalate';
  escalationTargets: string[];
  webhookDelivery?: {
    delivered: boolean;
    attemptedAt: string;
    statusCode?: number;
    message?: string;
  };
}

export interface EngineeringLoopInvestorReportAutomation {
  enabled: boolean;
  dayOfWeek: number;
  hourUtc: number;
  minuteUtc: number;
  windowDays: number;
  includeTrustLedger: boolean;
  recipients: string[];
  webhookUrl?: string;
  lastGeneratedAt?: string;
  nextRunAt?: string;
}

export interface EngineeringLoopInvestorScorecard {
  overall: number;
  velocity: number;
  quality: number;
  reliability: number;
  trust: number;
}

export interface EngineeringLoopInvestorReport {
  id: string;
  generatedAt: string;
  trigger: 'manual' | 'scheduled';
  windowDays: number;
  scorecard: EngineeringLoopInvestorScorecard;
  predictor: EngineeringLoopFailurePredictor;
  metrics: EngineeringLoopMetrics;
  governance: EngineeringLoopGovernanceSummary;
  topRuns: Array<{
    id: string;
    title: string;
    workflow: EngineeringLoopWorkflow;
    status: EngineeringLoopRunStatus;
    trustScore: number;
    updatedAt: string;
  }>;
  trustLedgerHighlights: Array<{
    runId: string;
    runTitle: string;
    stepId: string;
    phase: EngineeringLoopStep['phase'];
    stepTitle: string;
    status: EngineeringLoopStepStatus;
    riskLevel: EngineeringLoopRiskLevel;
    why: string;
    requiresHumanApproval: boolean;
    evidenceCount: number;
  }>;
  summary: {
    headline: string;
    investorNarrative: string;
  };
  delivery?: {
    delivered: boolean;
    attemptedAt: string;
    statusCode?: number;
    message?: string;
    webhookUrl?: string;
  };
}

export interface EngineeringLoopIssueToPrAutopilot {
  ticketUrl: string;
  ticketTitle: string;
  branch: string;
  codeChanges: Array<{
    file: string;
    changeType: 'create' | 'update' | 'delete';
    summary: string;
    rationale: string;
  }>;
  tests: Array<{
    name: string;
    command: string;
    expected: string;
  }>;
  riskReport: {
    level: EngineeringLoopRiskLevel;
    score: number;
    concerns: string[];
    mitigations: string[];
  };
  draftPr: {
    title: string;
    body: string;
    checklist: string[];
  };
}

export interface EngineeringLoopGuardianReport {
  policyProfile: 'strict' | 'standard' | 'lenient';
  scannerMode: 'realtime-plus-on-demand';
  findings: {
    static: Array<{
      id: string;
      category: 'static' | 'dependency' | 'architecture';
      severity: EngineeringLoopRiskLevel;
      title: string;
      detail: string;
      location?: string;
      policyRule: string;
      suggestedFix: string;
    }>;
    dependency: Array<{
      id: string;
      category: 'static' | 'dependency' | 'architecture';
      severity: EngineeringLoopRiskLevel;
      title: string;
      detail: string;
      location?: string;
      policyRule: string;
      suggestedFix: string;
    }>;
    architectureDrift: Array<{
      id: string;
      category: 'static' | 'dependency' | 'architecture';
      severity: EngineeringLoopRiskLevel;
      title: string;
      detail: string;
      location?: string;
      policyRule: string;
      suggestedFix: string;
    }>;
  };
  summary: {
    total: number;
    highOrCritical: number;
    riskScore: number;
  };
  oneClickFix: {
    actionLabel: string;
    patches: Array<{
      file: string;
      summary: string;
      policyRule: string;
    }>;
    autoValidation: {
      commands: string[];
      passCriteria: string[];
    };
  };
}

export interface EngineeringLoopReleaseIntelligence {
  releaseTag: string;
  environment: string;
  preDeploy: {
    riskForecast: {
      level: EngineeringLoopRiskLevel;
      score: number;
      confidence: number;
      drivers: string[];
    };
    blastRadius: {
      services: string[];
      downstream: string[];
      userSurfaces: string[];
    };
    rollbackScript: {
      shell: string[];
      manualChecks: string[];
    };
    stakeholderSummary: {
      executive: string;
      engineering: string;
      support: string;
    };
  };
  postDeploy: {
    anomalyWatch: {
      windowMinutes: number;
      signals: Array<{
        name: string;
        threshold: string;
        owner: string;
      }>;
      pagingPolicy: string;
    };
    incidentTimeline: Array<{
      offset: string;
      event: string;
      status: 'watch' | 'investigate' | 'mitigate' | 'resolved';
    }>;
  };
}

export type AutoDoerSource = 'manual' | 'github' | 'slack' | 'email' | 'whatsapp';
export type AutoDoerTaskType = 'fix-bug' | 'reply-email' | 'schedule-meeting' | 'deploy-code' | 'general';
export type AutoDoerRisk = 'low' | 'medium' | 'high' | 'critical';
export type AutoDoerTaskStatus = 'queued' | 'awaiting-approval' | 'running' | 'completed' | 'failed' | 'cancelled';
export type AutoDoerStepStatus = 'pending' | 'running' | 'completed' | 'failed' | 'blocked';
export type AutoDoerTimelineType =
  | 'ingested'
  | 'classified'
  | 'queued'
  | 'approval-requested'
  | 'approval-granted'
  | 'approval-rejected'
  | 'step-started'
  | 'step-completed'
  | 'step-failed'
  | 'completed'
  | 'failed';

export interface AutoDoerTimelineEvent {
  id: string;
  at: string;
  type: AutoDoerTimelineType;
  message: string;
  details?: Record<string, unknown>;
}

export interface AutoDoerStep {
  id: string;
  order: number;
  label: string;
  kind: 'orchestrator-intent' | 'adapter-action';
  status: AutoDoerStepStatus;
  intentKey?: string;
  action?: 'reply-email' | 'schedule-meeting' | 'deploy-code';
  input: string;
  startedAt?: string;
  completedAt?: string;
  output?: unknown;
  error?: string;
}

export interface AutoDoerApprovalState {
  required: boolean;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt?: string;
  decidedAt?: string;
  note?: string;
}

export interface AutoDoerTask {
  id: string;
  userId: string;
  source: AutoDoerSource;
  title: string;
  content: string;
  metadata: Record<string, unknown>;
  type: AutoDoerTaskType;
  risk: AutoDoerRisk;
  status: AutoDoerTaskStatus;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  approval: AutoDoerApprovalState;
  steps: AutoDoerStep[];
  timeline: AutoDoerTimelineEvent[];
  error?: string;
}

export interface AutoDoerPolicySummary {
  mode: 'supervised' | 'autonomous';
  allowRealDeployments: boolean;
  requiresApprovalFor: AutoDoerTaskType[];
  blockedWithoutApproval: AutoDoerTaskType[];
}

export interface AutomationEmailStatus {
  connected: boolean;
  email: string | null;
  maskedEmail: string | null;
  permissionGranted: boolean;
  verifiedAt: string | null;
  pendingVerification: boolean;
  expiresAt: string | null;
  lastOtpSentAt: string | null;
  nextAllowedResendAt: string | null;
  deliveryMode: 'preview' | 'smtp';
}

export interface AutomationEmailStartResult {
  status: AutomationEmailStatus;
  message: string;
  otpPreview: string;
}

export interface AutomationIntegrationHealth {
  construction: {
    active: boolean;
    label: string;
    message: string;
  };
  realtime: {
    enabledByConfig: boolean;
  };
  otp: {
    deliveryMode: 'preview' | 'smtp';
    deliveryPreference: string;
    smtpConfigured: boolean;
  };
  slack: {
    signingSecretConfigured: boolean;
    systemUserConfigured: boolean;
    autoExecute: boolean;
    dryRun: boolean;
    webhookPath: string;
  };
}

class ApiClient {
  /** Tracks when the current rate-limit cooldown expires so we only fire one event per window */
  private _rateLimitUntil = 0;
  /** Global network timeout for API calls so UI states can't hang indefinitely */
  private readonly requestTimeoutMs = 20_000;
  /** AI generation can take longer than standard API calls. */
  private readonly aiRequestTimeoutMs = 120_000;

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private invalidateAuthSession(message = 'Session expired. Please sign in again.'): ApiResponse<never> {
    this.clearAuthStorage();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('scrudix:auth-invalidated', {
          detail: { message },
        })
      );
    }
    return { success: false, error: message };
  }

  private getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.token);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.refreshToken);
  }

  private clearAuthStorage(): void {
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
    localStorage.removeItem(STORAGE_KEYS.auth);
    localStorage.removeItem(STORAGE_KEYS.user);
    localStorage.removeItem('scrudix_workspace_choice_required');
    localStorage.removeItem('scrudix_oauth_just_connected');
    localStorage.removeItem('scrudix_google_connected');
    localStorage.removeItem('scrudix_github_connected');
  }

  private getHeaders(includeAuth = false): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      const token = this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    includeAuth = false,
    _retried = false,
    requestTimeoutMs = this.requestTimeoutMs
  ): Promise<ApiResponse<T>> {
    const method = (options.method || 'GET').toUpperCase();
    const canRetrySafely = method === 'GET' || method === 'HEAD';

    let timedOut = false;
    const controller = new AbortController();
    const inheritedSignal = options.signal;
    const abortFromInheritedSignal = () => {
      controller.abort(
        inheritedSignal?.reason || new DOMException('Request aborted', 'AbortError')
      );
    };

    if (inheritedSignal) {
      if (inheritedSignal.aborted) {
        abortFromInheritedSignal();
      } else {
        inheritedSignal.addEventListener('abort', abortFromInheritedSignal, { once: true });
      }
    }

    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort(new DOMException('Request timed out', 'TimeoutError'));
    }, requestTimeoutMs);

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        signal: controller.signal,
        headers: {
          ...this.getHeaders(includeAuth),
          ...options.headers,
        },
      });

      // Auto-refresh on 401 (expired JWT) - attempt once when a token exists.
      // Feature endpoints can return 401 independently; only auth endpoints
      // should tear down the whole browser session when refresh fails.
      const isAuthEndpoint = endpoint.startsWith('/auth/');
      if (response.status === 401 && includeAuth && !_retried && this.getToken()) {
        const refreshResult = await this.refreshToken();
        if (refreshResult.success && refreshResult.data?.token) {
          safeSetItem(STORAGE_KEYS.token, refreshResult.data.token);
          if (refreshResult.data.refreshToken) {
            safeSetItem(STORAGE_KEYS.refreshToken, refreshResult.data.refreshToken);
          }
          return this.request<T>(endpoint, options, includeAuth, true, requestTimeoutMs);
        }
        // Refresh failed — only invalidate on real auth rejection, not on
        // transient backend unavailability (503).
        if (refreshResult.error?.includes('temporarily unavailable')) {
          return { success: false, error: refreshResult.error };
        }
        if (isAuthEndpoint) {
          return this.invalidateAuthSession();
        }
      }

      // Rate limit detection — parse Retry-After and dispatch a single event per cooldown window
      if (response.status === 429) {
        const retryAfterHeader = response.headers.get('Retry-After');
        const retryAfterSeconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : 30;
        const retryAt = Date.now() + retryAfterSeconds * 1000;

        // Peek at the body to distinguish a daily-quota 429 from a transient burst 429.
        const data429 = await response.json().catch(() => ({} as Record<string, unknown>));
        const errObj = (data429 as { error?: unknown }).error;
        const errCode =
          typeof errObj === 'object' && errObj !== null
            ? (errObj as { code?: unknown }).code
            : undefined;
        const isDailyLimit = errCode === 'DAILY_LIMIT_EXCEEDED';

        // Transient burst limits are safe to auto-retry once; daily quotas are not
        // (they reset at UTC midnight, so retrying immediately would just waste a call).
        if (!_retried && canRetrySafely && !isDailyLimit) {
          await this.sleep(Math.max(1000, Math.min(10_000, retryAfterSeconds * 1000)));
          return this.request<T>(endpoint, options, includeAuth, true, requestTimeoutMs);
        }

        // Only dispatch if we're not already inside an active cooldown
        if (Date.now() >= this._rateLimitUntil || isDailyLimit) {
          if (isDailyLimit) this._rateLimitUntil = retryAt;
          window.dispatchEvent(new CustomEvent('scrudix:rate-limited', {
            detail: { retryAfterSeconds, retryAt, endpoint, dailyLimit: isDailyLimit },
          }));
        }

        const serverMessage =
          typeof errObj === 'string'
            ? errObj
            : typeof (errObj as { message?: unknown })?.message === 'string'
              ? (errObj as { message: string }).message
              : isDailyLimit
                ? `Daily AI query limit reached. Resets in ${retryAfterSeconds}s.`
                : `Rate limited. Retry in ${retryAfterSeconds}s.`;
        return { success: false, error: serverMessage };
      }

      const data = await response.json().catch(() => ({} as Record<string, unknown>));

      if (!response.ok) {
        const errorMessage =
          typeof (data as { error?: unknown }).error === 'string'
            ? ((data as { error: string }).error)
            : typeof (data as { error?: { message?: unknown } }).error?.message === 'string'
              ? ((data as { error: { message: string } }).error.message)
              : `Request failed (${response.status})`;

        const normalizedError = errorMessage.toLowerCase();
        // Only invalidate session on auth-specific endpoints (/auth/me, /auth/login).
        // A non-auth endpoint returning 401 means the endpoint itself has an issue
        // (permissions, data not found, etc.) — not a session problem.
        const isStaleSessionError =
          includeAuth &&
          isAuthEndpoint &&
          (
            response.status === 401
            || (response.status === 404 && normalizedError.includes('user not found'))
            || normalizedError.includes('invalid token')
            || normalizedError.includes('no authorization token provided')
            || normalizedError.includes('unauthorized')
          );

        if (isStaleSessionError) {
          return this.invalidateAuthSession();
        }

        return { success: false, error: errorMessage };
      }

      return data;
    } catch (error) {
      const rawMessage = error instanceof Error ? error.message : 'Network error';
      if (!_retried && canRetrySafely && (timedOut || rawMessage === 'Failed to fetch')) {
        await this.sleep(timedOut ? 500 : 350);
        return this.request<T>(endpoint, options, includeAuth, true, requestTimeoutMs);
      }

      const message =
        timedOut
          ? 'Request timed out. Please retry.'
          : rawMessage === 'Failed to fetch'
          ? 'Cannot reach backend API. Start the backend server and retry.'
          : rawMessage;
      if ((import.meta as any).env?.DEV) {
        console.error(`[API] request failed for ${endpoint}: ${message}`);
      }
      return {
        success: false,
        error: message,
      };
    } finally {
      clearTimeout(timeoutId);
      if (inheritedSignal) {
        inheritedSignal.removeEventListener('abort', abortFromInheritedSignal);
      }
    }
  }

  // Authentication
  async register(name: string, email: string, password: string) {
    const response = await this.request<{ user: any; token: string; refreshToken?: string; existingUser?: boolean }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password }),
    });

    if (response.success && response.data) {
      safeSetItem(STORAGE_KEYS.token, response.data.token);
      if (response.data.refreshToken) {
        safeSetItem(STORAGE_KEYS.refreshToken, response.data.refreshToken);
      }
      safeSetItem(STORAGE_KEYS.auth, 'true');
      safeSetItem(STORAGE_KEYS.user, JSON.stringify(response.data.user));
    }

    return response;
  }

  async login(email: string, password: string) {
    const response = await this.request<{ user: any; token: string; refreshToken?: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.success && response.data) {
      safeSetItem(STORAGE_KEYS.token, response.data.token);
      if (response.data.refreshToken) {
        safeSetItem(STORAGE_KEYS.refreshToken, response.data.refreshToken);
      }
      safeSetItem(STORAGE_KEYS.auth, 'true');
      safeSetItem(STORAGE_KEYS.user, JSON.stringify(response.data.user));
    }

    return response;
  }

  async getOAuthUrl(provider: 'google' | 'github' | 'slack' | 'jira') {
    const params = new URLSearchParams();
    if (typeof window !== 'undefined') {
      params.set('frontendUrl', window.location.origin);
      params.set('returnTo', window.location.pathname || '/');
    }

    const query = params.toString();
    return this.request<{ provider: string; url: string }>(
      `/auth/oauth/${provider}/start${query ? `?${query}` : ''}`,
      {
        method: 'GET',
      }
    );
  }

  async getCurrentUser() {
    return this.request<{ user: any }>('/auth/me', {}, true);
  }

  async getUserPreferences() {
    return this.request<Array<{ key: string; value: string }>>('/auth/preferences', {}, true);
  }

  async setUserPreference(key: string, value: string) {
    return this.request<{ key: string; value: string }>('/auth/preferences', {
      method: 'POST',
      body: JSON.stringify({ key, value }),
    }, true);
  }

  async refreshToken() {
    const refreshToken = this.getRefreshToken();
    const token = this.getToken();
    if (!refreshToken && !token) {
      return { success: false, error: 'No token available' };
    }
    return this.request<{ token: string; refreshToken?: string }>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({
        ...(refreshToken ? { refreshToken } : {}),
        ...(token ? { token } : {}),
      }),
    });
  }

  async updateProfile(
    name?: string,
    profilePicture?: string | null,
    extra?: Partial<{
      bio: string;
      location: string;
      website: string;
      banner: string;
      skills: string[];
      githubUsername: string;
      socials: Record<string, string>;
      phone: string;
      workEmail: string;
      projects: Array<{
        id: string;
        name: string;
        description: string;
        icon?: string;
        tags?: string[];
        url?: string;
      }>;
    }>
  ) {
    const response = await this.request<{ user: any }>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify({ name, profilePicture, ...extra }),
    }, true);

    // Update localStorage if successful
    if (response.success && response.data) {
      safeSetItem('scrudix_user', JSON.stringify(response.data.user));
      if (profilePicture !== undefined) {
        if (profilePicture) {
          safeSetItem('scrudix_profile_picture', profilePicture);
        } else {
          localStorage.removeItem('scrudix_profile_picture');
        }
      }
    }

    return response;
  }

  async verifyProfile(payload: { phone: string; workEmail: string }) {
    return this.request<{ user: any }>('/auth/profile/verify', {
      method: 'POST',
      body: JSON.stringify(payload),
    }, true);
  }

  async trackActiveDay() {
    return this.request<{ activeDays: number; alreadyTrackedToday: boolean }>('/auth/track-day', {
      method: 'POST',
    }, true);
  }

  // User Projects
  async getUserProjects() {
    return this.request<{ projects: UserProject[] }>('/projects', {
      method: 'GET',
    }, true);
  }

  async createUserProject(project: Omit<UserProject, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) {
    return this.request<{ project: UserProject }>('/projects', {
      method: 'POST',
      body: JSON.stringify(project),
    }, true);
  }

  async updateUserProject(id: string, project: Partial<Omit<UserProject, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>) {
    return this.request<{ project: UserProject }>(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(project),
    }, true);
  }

  async deleteUserProject(id: string) {
    return this.request<{ deleted: boolean }>(`/projects/${id}`, {
      method: 'DELETE',
    }, true);
  }

  async reorderUserProjects(orderedIds: string[]) {
    return this.request<{ projects: UserProject[] }>('/projects/reorder', {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
    }, true);
  }

  // Code Analysis
  async analyzeCode(code: string, fileName?: string, language?: string, context?: string) {
    return this.request<{ review: string }>(
      '/code/review-pr',
      {
        method: 'POST',
        body: JSON.stringify({ code, fileName, language, context }),
      },
      true
    );
  }

  async explainCode(code: string, language?: string) {
    return this.request<{ explanation: string }>(
      '/code/explain',
      {
        method: 'POST',
        body: JSON.stringify({ code, language }),
      },
      true
    );
  }

  async suggestRefactoring(code: string, language?: string, goal?: string) {
    return this.request<{ originalCode: string; suggestion: string }>(
      '/code/refactor',
      {
        method: 'POST',
        body: JSON.stringify({ code, language, goal }),
      },
      true
    );
  }

  // Debugging
  async debugCode(error: string, code: string, stackTrace?: string) {
    return this.request<{ diagnosis: string; fix: string }>(
      '/debug/diagnose',
      {
        method: 'POST',
        body: JSON.stringify({ error, code, stackTrace }),
      },
      true
    );
  }

  // Legal Analysis
  async analyzeLegalDocument(document: string, documentType?: string) {
    return this.request<{ analysis: string; risks: string[]; obligations: string[] }>(
      '/legal/analyze',
      {
        method: 'POST',
        body: JSON.stringify({ documentText: document, documentType }),
      },
      true
    );
  }

  // QA Testing
  async generateTests(code: string, language?: string, framework?: string) {
    return this.request<{ tests: string; coverage: number }>(
      '/qa/generate',
      {
        method: 'POST',
        body: JSON.stringify({ code, filePath: 'input.ts', language, testFramework: framework }),
      },
      true
    );
  }

  // Competitive Intelligence
  async trackCompetitor(companyName: string, url?: string) {
    return this.request<{ success: boolean }>(
      '/competitive/track',
      {
        method: 'POST',
        body: JSON.stringify({ companyName, url }),
      },
      true
    );
  }

  async getCompetitorInsights(companyName?: string) {
    const endpoint = companyName 
      ? `/competitive/insights?company=${encodeURIComponent(companyName)}`
      : '/competitive/insights';
    return this.request<{ insights: any[] }>(endpoint, {}, true);
  }

  // Knowledge Management
  async saveKnowledge(title: string, content: string, tags?: string[]) {
    return this.request<{ id: string }>(
      '/knowledge/save',
      {
        method: 'POST',
        body: JSON.stringify({ title, content, tags }),
      },
      true
    );
  }

  async searchKnowledge(query: string) {
    return this.request<{ results: any[] }>(
      `/knowledge/search?q=${encodeURIComponent(query)}`,
      {},
      true
    );
  }

  // Editor integrations
  async getExtensionToken(rotate = false) {
    return this.request<{ token: string; type: string }>(
      '/auth/extension-token',
      {
        method: 'POST',
        body: JSON.stringify({ rotate }),
      },
      true
    );
  }

  async connectEditor(editor: 'vscode' | 'jetbrains' | 'neovim' | 'cursor' | 'other', context?: {
    workspaceCount?: number;
    workspaceName?: string;
  }) {
    return this.request<{
      editor: string;
      connected: boolean;
      availableActions: string[];
    }>(
      '/editor/connect',
      {
        method: 'POST',
        body: JSON.stringify({
          editor,
          workspaceCount: context?.workspaceCount,
          workspaceName: context?.workspaceName,
        }),
      },
      true
    );
  }

  async disconnectEditor(editor: 'vscode' | 'jetbrains' | 'neovim' | 'cursor' | 'other') {
    return this.request<{ editor: string; connected: boolean }>(
      '/editor/disconnect',
      {
        method: 'POST',
        body: JSON.stringify({ editor }),
      },
      true
    );
  }

  async getEditorStatus(editor?: 'vscode' | 'jetbrains' | 'neovim' | 'cursor' | 'other') {
    const query = editor ? `?editor=${encodeURIComponent(editor)}` : '';
    return this.request<{
      connected: boolean;
      sessions: Array<{
        editor: string;
        connected: boolean;
        lastSeenAt: string;
        workspaceCount: number;
      }>;
      metrics: {
        activeWorkspaces: number;
        actionsToday: number;
        inlineSuggestionsToday: number;
        timeSavedHours: number;
      };
      recentActivity: Array<{
        id: string;
        title: string;
        status: string;
        createdAt: string;
        details?: Record<string, unknown>;
      }>;
    }>(`/editor/status${query}`, {}, true);
  }

  async sendEditorEvent(
    editor: 'vscode' | 'jetbrains' | 'neovim' | 'cursor' | 'other',
    action: string,
    payload?: Record<string, unknown>
  ) {
    return this.request<{ received: boolean; activityId: string }>(
      '/editor/event',
      {
        method: 'POST',
        body: JSON.stringify({
          editor,
          action,
          payload,
          filePath: typeof payload?.filePath === 'string' ? payload.filePath : undefined,
          workspaceName: typeof payload?.workspaceName === 'string' ? payload.workspaceName : undefined,
          workspaceCount: typeof payload?.workspaceCount === 'number' ? payload.workspaceCount : undefined,
        }),
      },
      true
    );
  }

  // External integrations (GitHub / Slack / Jira)
  async getIntegrations() {
    return this.request<{
      connections: Array<{
        provider: 'github' | 'slack' | 'jira';
        connected: boolean;
        connectedAt: string | null;
        updatedAt: string | null;
      }>;
    }>('/integrations', {}, true);
  }

  async connectIntegration(
    provider: 'github' | 'slack' | 'jira',
    metadata?: { login?: string; profileUrl?: string; workspace?: string }
  ) {
    return this.request<{
      provider: 'github' | 'slack' | 'jira';
      connections: Array<{
        provider: 'github' | 'slack' | 'jira';
        connected: boolean;
        connectedAt: string | null;
        updatedAt: string | null;
        login: string | null;
        profileUrl: string | null;
        workspace: string | null;
      }>;
    }>(
      '/integrations/connect',
      {
        method: 'POST',
        body: JSON.stringify({ provider, ...metadata }),
      },
      true
    );
  }

  async disconnectIntegration(provider: 'github' | 'slack' | 'jira') {
    return this.request<{
      provider: 'github' | 'slack' | 'jira';
      connections: Array<{
        provider: 'github' | 'slack' | 'jira';
        connected: boolean;
        connectedAt: string | null;
        updatedAt: string | null;
        login: string | null;
        profileUrl: string | null;
        workspace: string | null;
      }>;
    }>(
      '/integrations/disconnect',
      {
        method: 'POST',
        body: JSON.stringify({ provider }),
      },
      true
    );
  }

  // Dashboard automation email OTP flow
  async getAutomationEmailStatus() {
    return this.request<AutomationEmailStatus>('/automation/email/status', {}, true);
  }

  async getAutomationHealth() {
    return this.request<AutomationIntegrationHealth>('/automation/health', {}, true);
  }

  async startAutomationEmailOtp(input: {
    email: string;
    permissionGranted: boolean;
  }) {
    return this.request<AutomationEmailStartResult>(
      '/automation/email/start',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async resendAutomationEmailOtp(email?: string) {
    return this.request<AutomationEmailStartResult>(
      '/automation/email/resend',
      {
        method: 'POST',
        body: JSON.stringify(email ? { email } : {}),
      },
      true
    );
  }

  async verifyAutomationEmailOtp(otp: string) {
    return this.request<{
      status: AutomationEmailStatus;
      message: string;
    }>(
      '/automation/email/verify',
      {
        method: 'POST',
        body: JSON.stringify({ otp }),
      },
      true
    );
  }

  async disconnectAutomationEmail() {
    return this.request<{
      status: AutomationEmailStatus;
      message: string;
    }>(
      '/automation/email/disconnect',
      {
        method: 'POST',
      },
      true
    );
  }

  // Analytics sync
  async sendAnalyticsEvent(input: {
    type: 'page_view' | 'event';
    name: string;
    at?: string;
    clientEventId?: string;
    payload?: Record<string, string | number | boolean | null>;
  }) {
    return this.request<{
      event: {
        id: string;
        type: 'page_view' | 'event';
        name: string;
        at: string;
        payload?: Record<string, string | number | boolean | null>;
      };
    }>(
      '/analytics/event',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async getAnalyticsEvents(limit = 60) {
    return this.request<{
      events: Array<{
        id: string;
        type: 'page_view' | 'event';
        name: string;
        at: string;
        payload?: Record<string, string | number | boolean | null>;
        clientEventId?: string;
      }>;
    }>(`/analytics/events?limit=${Math.max(1, Math.min(250, Math.floor(limit)))}`, {}, true);
  }

  async getAnalyticsFunnel() {
    return this.request<{
      generatedAt: string;
      totalEvents: number;
      steps: Array<{
        id: string;
        label: string;
        count: number;
        conversion: number;
      }>;
    }>('/analytics/funnel', {}, true);
  }

  async clearAnalyticsEvents() {
    return this.request<{ removed: number }>(
      '/analytics/events',
      {
        method: 'DELETE',
      },
      true
    );
  }

  async getActivityHeatmap() {
    return this.request<{
      days: Array<{ date: string; count: number }>;
      streak: number;
      totalActivities: number;
      activeDays: number;
    }>('/analytics/heatmap', {}, true);
  }

  async getActivityGraph() {
    return this.request<{
      days: Array<{ date: string; code: number; debug: number; qa: number; other: number; total: number }>;
      rangeDays: number;
    }>('/analytics/activity-graph', {}, true);
  }

  async getUsageReports(windowDays = 30) {
    const safeWindowDays = Math.max(7, Math.min(90, Math.floor(windowDays)));
    return this.request<{
      generatedAt: string;
      windowDays: number;
      reports: Array<{
        id: string;
        title: string;
        type: 'Engineering' | 'Adoption' | 'Engagement' | 'Integrations';
        generatedAt: string;
        metric: string;
        summary: string;
        details: string[];
      }>;
    }>(`/analytics/usage-reports?windowDays=${safeWindowDays}`, {}, true);
  }

  async getContinuityCommandCenter(windowDays = 56) {
    const safeWindowDays = Math.max(7, Math.min(180, Math.floor(windowDays)));
    return this.request<{
      generatedAt: string;
      windowDays: number;
      readinessTier: 'exploring' | 'contender' | 'breakout' | 'future-defining';
      headline: string;
      summary: string;
      scorecard: {
        reports: number;
        outcomes: number;
        criteriaMet: number;
        criteriaTotal: number;
        momentumTrend: 'up' | 'down' | 'flat' | 'unknown';
        momentumDelta?: number;
        forecastConfidence?: number;
      };
      trendline: Array<{
        label: string;
        score?: number;
        criteriaMet: number;
        criteriaTotal: number;
      }>;
      teamQueues: Array<{
        owner: 'engineering' | 'platform' | 'product' | 'leadership';
        activeActions: number;
        p0Actions: number;
        topObjective?: string;
      }>;
      topPlaybook: Array<{
        id: string;
        priority: 'P0' | 'P1' | 'P2';
        objective: string;
        action: string;
        owner: 'engineering' | 'platform' | 'product' | 'leadership';
        dueInDays: number;
      }>;
    }>(`/debug/continuity-command-center?windowDays=${safeWindowDays}`, {}, true);
  }

  async getContinuityDigest(windowDays = 56) {
    const safeWindowDays = Math.max(7, Math.min(180, Math.floor(windowDays)));
    return this.request<{
      generatedAt: string;
      windowDays: number;
      subject: string;
      executiveSummary: string;
      highlights: string[];
      risks: string[];
      actions: string[];
      markdown: string;
      metrics: {
        generatedAt: string;
        windowDays: number;
        continuityReports: number;
        continuityOutcomes: number;
        readinessTier?: 'exploring' | 'contender' | 'breakout' | 'future-defining';
        criteria: Array<{
          key:
            | 'median_time_to_root_cause_reduction'
            | 'continuity_end_to_end_usage'
            | 'week4_retention'
            | 'struggle_without_scrudix'
            | 'accepted_fix_no_major_rewrite';
          target: number;
          value?: number;
          met: boolean;
          unit: 'percent';
          status: 'met' | 'missed' | 'unknown';
          note: string;
        }>;
      };
      commandCenter: {
        generatedAt: string;
        windowDays: number;
        readinessTier: 'exploring' | 'contender' | 'breakout' | 'future-defining';
        headline: string;
        summary: string;
        scorecard: {
          reports: number;
          outcomes: number;
          criteriaMet: number;
          criteriaTotal: number;
          momentumTrend: 'up' | 'down' | 'flat' | 'unknown';
          momentumDelta?: number;
          forecastConfidence?: number;
        };
        trendline: Array<{
          label: string;
          score?: number;
          criteriaMet: number;
          criteriaTotal: number;
        }>;
        teamQueues: Array<{
          owner: 'engineering' | 'platform' | 'product' | 'leadership';
          activeActions: number;
          p0Actions: number;
          topObjective?: string;
        }>;
        topPlaybook: Array<{
          id: string;
          priority: 'P0' | 'P1' | 'P2';
          objective: string;
          action: string;
          owner: 'engineering' | 'platform' | 'product' | 'leadership';
          dueInDays: number;
        }>;
      };
    }>(`/debug/continuity-digest?windowDays=${safeWindowDays}`, {}, true);
  }

  async getContinuityInvestorReadiness(windowDays = 56) {
    const safeWindowDays = Math.max(7, Math.min(180, Math.floor(windowDays)));
    return this.request<{
      generatedAt: string;
      windowDays: number;
      readinessTier: 'exploring' | 'contender' | 'breakout' | 'future-defining';
      summary: string;
      topInvestorNarrative: string;
      benchmarkPosition: 'emerging' | 'contender' | 'leader';
      scores: {
        investorReadiness: number;
        wedge: number;
        roiProof: number;
        defensibility: number;
        executionConfidence: number;
      };
      proof: {
        criteriaMet: number;
        criteriaTotal: number;
        telemetryCoverage: number;
        momentumTrend: 'up' | 'down' | 'flat' | 'unknown';
        momentumDelta?: number;
        forecastConfidence?: number;
      };
      roi: {
        incidentsCaptured: number;
        outcomesCaptured: number;
        estimatedHoursSavedPerWeek: number;
        estimatedAnnualValueUsd: number;
        paybackDays: number;
        assumptions: string[];
      };
      moat: {
        operatorDependenceRate?: number;
        week4RetentionRate?: number;
        acceptedFixNoMajorRewriteRate?: number;
        defensibilitySignals: string[];
      };
      benchmarkSignals: string[];
      topPriorities: Array<{
        priority: 'P0' | 'P1' | 'P2';
        title: string;
        action: string;
        owner: 'engineering' | 'platform' | 'product' | 'leadership';
        dueInDays: number;
      }>;
    }>(`/debug/continuity-investor-readiness?windowDays=${safeWindowDays}`, {}, true);
  }

  // Dashboard workspace persistence
  async getWorkspaceSection<T extends Record<string, unknown>>(
    section: 'settings' | 'learning' | 'billing'
  ) {
    return this.request<{
      section: 'settings' | 'learning' | 'billing';
      state: T | null;
      updatedAt: string | null;
    }>(`/workspace/${section}`, {}, true);
  }

  async saveWorkspaceSection<T extends Record<string, unknown>>(
    section: 'settings' | 'learning' | 'billing',
    state: T
  ) {
    return this.request<{
      section: 'settings' | 'learning' | 'billing';
      state: T;
      updatedAt: string;
    }>(
      `/workspace/${section}`,
      {
        method: 'PUT',
        body: JSON.stringify({ state }),
      },
      true
    );
  }

  async getSettingsWorkspace<T extends Record<string, unknown>>() {
    return this.getWorkspaceSection<T>('settings');
  }

  async saveSettingsWorkspace<T extends Record<string, unknown>>(state: T) {
    return this.saveWorkspaceSection('settings', state);
  }

  async getLearningWorkspace<T extends Record<string, unknown>>() {
    return this.getWorkspaceSection<T>('learning');
  }

  async saveLearningWorkspace<T extends Record<string, unknown>>(state: T) {
    return this.saveWorkspaceSection('learning', state);
  }

  async getBillingWorkspace<T extends Record<string, unknown>>() {
    return this.getWorkspaceSection<T>('billing');
  }

  async saveBillingWorkspace<T extends Record<string, unknown>>(state: T) {
    return this.saveWorkspaceSection('billing', state);
  }

  // ═══ Stripe Billing ═══════════════════════════════════════════
  async getBillingStatus() {
    return this.request<{
      plan: 'free' | 'pro' | 'proPlus' | 'enterprise';
      usage: {
        queriesThisMonth: number;
        hoursSaved: number;
        tasksCompleted: number;
        voiceChatsThisMonth: number;
        studioChatsThisMonth: number;
        usageMonth: string;
      };
      referralBonus?: {
        queries: number;
        codeReviews: number;
        documents: number;
        teamMembers: number;
      };
      stripeConfigured: boolean;
      razorpayConfigured?: boolean;
      paymentContext?: {
        country: string;
        currency: string;
        gateway: 'stripe' | 'razorpay' | 'none';
        supportedGateways: Array<'stripe' | 'razorpay' | 'none'>;
        autopay: boolean;
      };
      billingState: Record<string, unknown> | null;
    }>('/billing/status', {}, true);
  }

  async incrementBillingUsage(feature: 'voiceChat' | 'studioChat') {
    return this.request<{
      usage: {
        queriesThisMonth: number;
        hoursSaved: number;
        tasksCompleted: number;
        voiceChatsThisMonth: number;
        studioChatsThisMonth: number;
        usageMonth: string;
      };
    }>('/billing/usage/increment', {
      method: 'POST',
      body: JSON.stringify({ feature }),
    }, true);
  }

  async getBillingPaymentContext() {
    return this.request<{
      country: string;
      currency: string;
      gateway: 'stripe' | 'razorpay' | 'none';
      supportedGateways: Array<'stripe' | 'razorpay' | 'none'>;
      autopay: boolean;
    }>('/billing/payment-context', {}, false); // Public route - no auth required
  }

  async getBillingPricing(options?: {
    country?: string;
    currency?: string;
    gateway?: 'stripe' | 'razorpay' | 'none';
  }) {
    const query = new URLSearchParams();
    if (options?.country) query.set('country', options.country);
    if (options?.currency) query.set('currency', options.currency);
    if (options?.gateway) query.set('gateway', options.gateway);
    const suffix = query.toString() ? `?${query.toString()}` : '';

    return this.request<{
      context: {
        country: string;
        currency: string;
        gateway: 'stripe' | 'razorpay' | 'none';
        supportedGateways: Array<'stripe' | 'razorpay' | 'none'>;
        autopay: boolean;
      };
      plans: {
        free: { monthly: number; yearly: number };
        pro: { monthly: number; yearly: number; monthlyInr: number; yearlyInr: number };
        proPlus: { monthly: number; yearly: number; monthlyInr: number; yearlyInr: number };
        enterprise: { contactSales: true; contactEmail: string };
      };
    }>(`/billing/pricing${suffix}`, {}, false); // Public route - no auth required
  }

  async createCheckoutSession(
    planId: 'pro' | 'proPlus' | 'enterprise',
    cycle: 'monthly' | 'yearly',
    options?: {
      gateway?: 'stripe' | 'razorpay';
      currency?: string;
      country?: string;
      autopay?: boolean;
    }
  ) {
    return this.request<{
      mode: 'demo' | 'live' | 'contact-sales';
      gateway?: 'stripe' | 'razorpay' | 'none';
      currency?: string;
      country?: string;
      autopay?: boolean;
      checkoutUrl?: string;
      sessionId?: string;
      subscriptionId?: string;
      message?: string;
      plan?: string;
      cycle?: string;
      contactEmail?: string;
    }>(
      '/billing/create-checkout',
      { method: 'POST', body: JSON.stringify({ planId, cycle, ...options }) },
      true
    );
  }

  async createBillingPortal() {
    return this.request<{ portalUrl?: string; mode: string; message?: string }>(
      '/billing/create-portal',
      { method: 'POST' },
      true
    );
  }

  async getInvoices() {
    return this.request<{
      mode: 'demo' | 'live';
      invoices: Array<{ id: string; date: string; amount: number; status: string; plan: string; pdf?: string; hostedUrl?: string }>;
    }>('/billing/invoices', {}, true);
  }

  // ═══ Effective AI Config (from Settings) ═══════════════════════
  async getEffectiveAIConfig() {
    return this.request<{
      autoReviewPullRequests: boolean;
      generateDailyReports: boolean;
      realTimeCodeSuggestions: boolean;
      weeklyCompetitiveBriefings: boolean;
      notifications: Record<string, any>;
      quietHours: { enabled: boolean; start: string; end: string };
      updatedAt: string | null;
    }>('/workspace/effective-config/ai', {}, true);
  }

  // Intent-to-impact strategy planning
  async createGoalPlan(input: GoalPlanInput) {
    return this.request<{ planId: string; createdAt: string; plan: GoalPlan }>(
      '/strategy/plan',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async getGoalPlans(limit = 12) {
    const safeLimit = Math.max(1, Math.min(50, Math.floor(limit)));
    return this.request<{ plans: StoredGoalPlan[] }>(`/strategy/plans?limit=${safeLimit}`, {}, true);
  }

  async createExecutionPlan(planId: string, input: ExecutionPlanInput = {}) {
    return this.request<{
      executionId: string;
      planId: string;
      createdAt: string;
      input: ExecutionPlanInput;
      execution: ExecutionPlan;
      progress?: ExecutionProgressSnapshot | null;
    }>(
      `/strategy/plans/${encodeURIComponent(planId)}/execution`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async getExecutionPlans(planId: string, limit = 8) {
    const safeLimit = Math.max(1, Math.min(30, Math.floor(limit)));
    return this.request<{ executionPlans: StoredExecutionPlan[] }>(
      `/strategy/plans/${encodeURIComponent(planId)}/execution?limit=${safeLimit}`,
      {},
      true
    );
  }

  async getExecutionProgress(planId: string, executionId: string) {
    return this.request<{ progress: ExecutionProgressSnapshot }>(
      `/strategy/plans/${encodeURIComponent(planId)}/execution/${encodeURIComponent(executionId)}/progress`,
      {},
      true
    );
  }

  async updateExecutionTicketStatus(
    planId: string,
    executionId: string,
    ticketId: string,
    status: ExecutionTicketStatus,
    note?: string
  ) {
    return this.request<{ progress: ExecutionProgressSnapshot }>(
      `/strategy/plans/${encodeURIComponent(planId)}/execution/${encodeURIComponent(executionId)}/tickets/${encodeURIComponent(ticketId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status, note }),
      },
      true
    );
  }

  async updateExecutionReadiness(
    planId: string,
    executionId: string,
    input: {
      qaGateIndexesDone?: number[];
      checklistIndexesDone?: number[];
    }
  ) {
    return this.request<{ progress: ExecutionProgressSnapshot }>(
      `/strategy/plans/${encodeURIComponent(planId)}/execution/${encodeURIComponent(executionId)}/readiness`,
      {
        method: 'PATCH',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async syncExecutionIssues(
    planId: string,
    executionId: string,
    input: {
      provider: 'github' | 'jira';
      ticketIds?: string[];
      workspace?: string;
    }
  ) {
    return this.request<{
      provider: 'github' | 'jira';
      workspace: string;
      createdIssues: Array<{
        ticketId: string;
        provider: 'github' | 'jira';
        externalId: string;
        url: string;
        title: string;
      }>;
      progress: ExecutionProgressSnapshot;
    }>(
      `/strategy/plans/${encodeURIComponent(planId)}/execution/${encodeURIComponent(executionId)}/sync-issues`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  // Engineering loop orchestration
  async getEngineeringLoopTemplate(
    workflow:
      | 'pr-risk-gate'
      | 'incident-commander'
      | 'issue-to-pr-autopilot'
      | 'guardian-2'
      | 'release-intelligence'
  ) {
    return this.request<{
      workflow:
        | 'pr-risk-gate'
        | 'incident-commander'
        | 'issue-to-pr-autopilot'
        | 'guardian-2'
        | 'release-intelligence';
      title: string;
      autonomyModes: EngineeringLoopAutonomyMode[];
      defaultSteps: Array<{
        phase: EngineeringLoopStep['phase'];
        title: string;
        owner: EngineeringLoopStep['owner'];
        defaultRiskLevel: EngineeringLoopRiskLevel;
        requiresHumanApproval: boolean;
        validationPlan: string[];
      }>;
      successCriteria: string[];
      severityLevels?: string[];
      policyProfiles?: Array<'strict' | 'standard' | 'lenient'>;
    }>(`/engineering-loop/templates/${workflow}`, {}, true);
  }

  async createPrRiskGateRun(input: {
    prNumber?: string;
    title?: string;
    objective?: string;
    runTitle?: string;
    repository?: string;
    baseBranch?: string;
    headBranch?: string;
    changedFiles?: string[];
    labels?: string[];
    linesAdded?: number;
    linesDeleted?: number;
    autonomyMode?: EngineeringLoopAutonomyMode;
  }) {
    return this.request<{
      run: EngineeringLoopRun;
      gate: {
        inferredRisk: EngineeringLoopRiskLevel;
        changedFiles: number;
        linesChanged: number;
      };
    }>(
      '/engineering-loop/templates/pr-risk-gate/runs',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async createIncidentCommanderRun(input: {
    incidentTitle?: string;
    incidentSummary?: string;
    severity?: string;
    affectedServices?: string[];
    signals?: string[];
    activeAlerts?: string[];
    customerImpact?: boolean;
    dataLossRisk?: boolean;
    objective?: string;
    runTitle?: string;
    repository?: string;
    branch?: string;
    tags?: string[];
    autonomyMode?: EngineeringLoopAutonomyMode;
  }) {
    return this.request<{
      run: EngineeringLoopRun;
      incident: {
        title: string;
        summary?: string;
        severity?: string;
        inferredRisk: EngineeringLoopRiskLevel;
        affectedServices: string[];
        signals: string[];
        customerImpact: boolean;
        dataLossRisk: boolean;
      };
    }>(
      '/engineering-loop/templates/incident-commander/runs',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async createIssueToPrAutopilotRun(input: {
    ticketUrl: string;
    ticketTitle?: string;
    ticketSummary?: string;
    objective?: string;
    runTitle?: string;
    repository?: string;
    baseBranch?: string;
    labels?: string[];
    filesTouched?: string[];
    autonomyMode?: EngineeringLoopAutonomyMode;
  }) {
    return this.request<{
      run: EngineeringLoopRun;
      autopilot: EngineeringLoopIssueToPrAutopilot;
    }>(
      '/engineering-loop/templates/issue-to-pr-autopilot/runs',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async createGuardianTwoRun(input: {
    targetPaths?: string[];
    dependencySnapshot?: string[];
    architectureBaseline?: string[];
    policyProfile?: 'strict' | 'standard' | 'lenient';
    objective?: string;
    runTitle?: string;
    repository?: string;
    branch?: string;
    autonomyMode?: EngineeringLoopAutonomyMode;
  }) {
    return this.request<{
      run: EngineeringLoopRun;
      guardian: EngineeringLoopGuardianReport;
    }>(
      '/engineering-loop/templates/guardian-2/runs',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async createReleaseIntelligenceRun(input: {
    releaseTag?: string;
    version?: string;
    environment?: string;
    changedServices?: string[];
    changedFiles?: string[];
    deploymentWindow?: string;
    releaseNotes?: string;
    observabilitySignals?: string[];
    objective?: string;
    runTitle?: string;
    repository?: string;
    branch?: string;
    autonomyMode?: EngineeringLoopAutonomyMode;
  }) {
    return this.request<{
      run: EngineeringLoopRun;
      releaseIntelligence: EngineeringLoopReleaseIntelligence;
    }>(
      '/engineering-loop/templates/release-intelligence/runs',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async createEngineeringLoopRun(input: {
    workflow?: EngineeringLoopWorkflow;
    title?: string;
    objective: string;
    repository?: string;
    branch?: string;
    autonomyMode?: EngineeringLoopAutonomyMode;
    tags?: string[];
    stepSeeds?: Array<{
      phase?: EngineeringLoopStep['phase'];
      title?: string;
      owner?: EngineeringLoopStep['owner'];
      notes?: string;
      trust?: Partial<EngineeringLoopTrustMetadata>;
      artifacts?: EngineeringLoopArtifact[];
    }>;
  }) {
    return this.request<{ run: EngineeringLoopRun }>(
      '/engineering-loop/runs',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async getEngineeringLoopRuns(input?: {
    limit?: number;
    workflow?: EngineeringLoopWorkflow;
    status?: EngineeringLoopRunStatus;
  }) {
    const params = new URLSearchParams();
    const limit = Math.max(1, Math.min(100, Math.floor(input?.limit || 20)));
    params.set('limit', String(limit));
    if (input?.workflow) {
      params.set('workflow', input.workflow);
    }
    if (input?.status) {
      params.set('status', input.status);
    }

    return this.request<{ runs: EngineeringLoopRunSummary[] }>(
      `/engineering-loop/runs?${params.toString()}`,
      {},
      true
    );
  }

  async getEngineeringLoopMetrics(windowDays = 7) {
    const safeWindowDays = Math.max(1, Math.min(90, Math.floor(windowDays || 7)));
    return this.request<EngineeringLoopMetrics>(
      `/engineering-loop/metrics?windowDays=${safeWindowDays}`,
      {},
      true
    );
  }

  async getEngineeringLoopGovernanceSummary(windowDays = 7) {
    const safeWindowDays = Math.max(1, Math.min(90, Math.floor(windowDays || 7)));
    return this.request<EngineeringLoopGovernanceSummary>(
      `/engineering-loop/governance/summary?windowDays=${safeWindowDays}`,
      {},
      true
    );
  }

  async exportEngineeringLoopGovernance(windowDays = 7) {
    const safeWindowDays = Math.max(1, Math.min(90, Math.floor(windowDays || 7)));
    return this.request<{
      generatedAt: string;
      windowDays: number;
      summary: EngineeringLoopGovernanceSummary;
      runs: Array<{
        id: string;
        workflow: EngineeringLoopWorkflow;
        title: string;
        status: EngineeringLoopRunStatus;
        trustScore: number;
        updatedAt: string;
        stepSummary: Record<EngineeringLoopStepStatus, number>;
        policyExceptions: number;
      }>;
    }>(`/engineering-loop/governance/export?windowDays=${safeWindowDays}`, {}, true);
  }

  async getEngineeringLoopSignals(input?: {
    limit?: number;
    status?: EngineeringLoopSignalStatus;
    source?: EngineeringLoopSignalSource;
  }) {
    const params = new URLSearchParams();
    const limit = Math.max(1, Math.min(300, Math.floor(input?.limit || 40)));
    params.set('limit', String(limit));
    if (input?.status) {
      params.set('status', input.status);
    }
    if (input?.source) {
      params.set('source', input.source);
    }

    return this.request<{
      signals: EngineeringLoopCustomerSignal[];
      total: number;
    }>(`/engineering-loop/signals?${params.toString()}`, {}, true);
  }

  async createEngineeringLoopSignal(input: {
    source?: EngineeringLoopSignalSource;
    title: string;
    detail?: string;
    impact?: EngineeringLoopRiskLevel;
    status?: EngineeringLoopSignalStatus;
    externalId?: string;
    metadata?: Record<string, unknown>;
  }) {
    return this.request<{ signal: EngineeringLoopCustomerSignal }>(
      '/engineering-loop/signals',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async ingestEngineeringLoopSignals(input?: {
    providers?: Array<'github' | 'slack' | 'jira'>;
    limitPerProvider?: number;
  }) {
    return this.request<{
      providerSummaries: EngineeringLoopSignalIngestionSummary[];
      totalIngested: number;
      totalSkipped: number;
      signals: EngineeringLoopCustomerSignal[];
    }>(
      '/engineering-loop/signals/ingest',
      {
        method: 'POST',
        body: JSON.stringify(input || {}),
      },
      true
    );
  }

  async updateEngineeringLoopSignalStatus(signalId: string, status: EngineeringLoopSignalStatus) {
    return this.request<{ signal: EngineeringLoopCustomerSignal }>(
      `/engineering-loop/signals/${encodeURIComponent(signalId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      },
      true
    );
  }

  async getEngineeringFailurePredictor(windowDays = 14) {
    const safeWindowDays = Math.max(1, Math.min(90, Math.floor(windowDays || 14)));
    return this.request<{
      predictor: EngineeringLoopFailurePredictor;
      policy: EngineeringLoopFailureAlertPolicy;
    }>(`/engineering-loop/failure-predictor?windowDays=${safeWindowDays}`, {}, true);
  }

  async getEngineeringFailureAlerts(limit = 20) {
    const safeLimit = Math.max(1, Math.min(120, Math.floor(limit || 20)));
    return this.request<{ alerts: EngineeringLoopFailureAlert[] }>(
      `/engineering-loop/failure-alerts?limit=${safeLimit}`,
      {},
      true
    );
  }

  async getEngineeringFailureAlertPolicy() {
    return this.request<{ policy: EngineeringLoopFailureAlertPolicy }>(
      '/engineering-loop/failure-alerts/policy',
      {},
      true
    );
  }

  async updateEngineeringFailureAlertPolicy(input: Partial<EngineeringLoopFailureAlertPolicy>) {
    return this.request<{ policy: EngineeringLoopFailureAlertPolicy }>(
      '/engineering-loop/failure-alerts/policy',
      {
        method: 'PUT',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async getEngineeringInvestorReports(limit = 12) {
    const safeLimit = Math.max(1, Math.min(120, Math.floor(limit || 12)));
    return this.request<{ reports: EngineeringLoopInvestorReport[] }>(
      `/engineering-loop/investor-reports?limit=${safeLimit}`,
      {},
      true
    );
  }

  async getEngineeringInvestorReportAutomation() {
    return this.request<{
      automation: EngineeringLoopInvestorReportAutomation;
      latestReport?: EngineeringLoopInvestorReport;
    }>('/engineering-loop/investor-reports/automation', {}, true);
  }

  async updateEngineeringInvestorReportAutomation(
    input: Partial<EngineeringLoopInvestorReportAutomation>
  ) {
    return this.request<{ automation: EngineeringLoopInvestorReportAutomation }>(
      '/engineering-loop/investor-reports/automation',
      {
        method: 'PUT',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async generateEngineeringInvestorReport(input?: {
    windowDays?: number;
    deliver?: boolean;
    trigger?: 'manual' | 'scheduled';
  }) {
    return this.request<{
      report: EngineeringLoopInvestorReport;
      automation: EngineeringLoopInvestorReportAutomation;
    }>(
      '/engineering-loop/investor-reports/generate',
      {
        method: 'POST',
        body: JSON.stringify(input || {}),
      },
      true
    );
  }

  async getEngineeringLoopRun(runId: string) {
    return this.request<{ run: EngineeringLoopRun }>(
      `/engineering-loop/runs/${encodeURIComponent(runId)}`,
      {},
      true
    );
  }

  async updateEngineeringLoopStep(
    runId: string,
    stepId: string,
    input: {
      actor?: EngineeringLoopActorType;
      status?: EngineeringLoopStepStatus;
      notes?: string;
      trust?: Partial<EngineeringLoopTrustMetadata>;
      evidence?: EngineeringLoopTrustEvidence[];
      artifacts?: EngineeringLoopArtifact[];
    }
  ) {
    return this.request<{
      run: EngineeringLoopRun;
      step: EngineeringLoopStep;
      autoTeamBrainEntry?: EngineeringLoopTeamBrainEntry;
    }>(
      `/engineering-loop/runs/${encodeURIComponent(runId)}/steps/${encodeURIComponent(stepId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async updateEngineeringLoopStatus(
    runId: string,
    input: {
      status: EngineeringLoopRunStatus;
      actor?: EngineeringLoopActorType;
      prUrl?: string;
      note?: string;
    }
  ) {
    return this.request<{ run: EngineeringLoopRun; autoTeamBrainEntry?: EngineeringLoopTeamBrainEntry }>(
      `/engineering-loop/runs/${encodeURIComponent(runId)}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async createEngineeringLoopPolicyException(
    runId: string,
    input: {
      reason: string;
      approvedBy?: string;
      scope?: string;
      expiresAt?: string;
    }
  ) {
    return this.request<{
      run: EngineeringLoopRun;
      policyException: {
        id: string;
        reason: string;
        approvedBy?: string;
        scope: string;
        expiresAt?: string;
        createdAt: string;
      };
    }>(
      `/engineering-loop/runs/${encodeURIComponent(runId)}/policy-exception`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async getEngineeringTeamBrain(input?: {
    limit?: number;
    type?: EngineeringLoopTeamBrainType;
    q?: string;
  }) {
    const params = new URLSearchParams();
    const limit = Math.max(1, Math.min(200, Math.floor(input?.limit || 30)));
    params.set('limit', String(limit));
    if (input?.type) {
      params.set('type', input.type);
    }
    if (input?.q && input.q.trim()) {
      params.set('q', input.q.trim());
    }

    return this.request<{
      entries: EngineeringLoopTeamBrainEntry[];
      summary: {
        total: number;
        byType: Record<EngineeringLoopTeamBrainType, number>;
      };
    }>(`/engineering-loop/team-brain?${params.toString()}`, {}, true);
  }

  async createEngineeringTeamBrainEntry(input: {
    type: EngineeringLoopTeamBrainType;
    title: string;
    summary: string;
    signals?: string[];
    sourceRunId?: string;
    confidence?: number;
    tags?: string[];
    metadata?: Record<string, unknown>;
  }) {
    return this.request<{ entry: EngineeringLoopTeamBrainEntry }>(
      '/engineering-loop/team-brain',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async saveEngineeringRunToTeamBrain(
    runId: string,
    input?: {
      type?: EngineeringLoopTeamBrainType;
      title?: string;
      summary?: string;
      signals?: string[];
      tags?: string[];
      confidence?: number;
    }
  ) {
    return this.request<{ entry: EngineeringLoopTeamBrainEntry }>(
      `/engineering-loop/runs/${encodeURIComponent(runId)}/team-brain`,
      {
        method: 'POST',
        body: JSON.stringify(input || {}),
      },
      true
    );
  }

  // ─── Auto-Doer (execution-first autonomy) ──────────────────────────────────

  async getAutoDoerPolicy() {
    return this.request<AutoDoerPolicySummary>('/autodoer/policy', {}, true);
  }

  async listAutoDoerTasks(limit = 50) {
    const boundedLimit = Math.max(1, Math.min(200, Math.floor(limit || 50)));
    return this.request<AutoDoerTask[]>(`/autodoer/tasks?limit=${boundedLimit}`, {}, true);
  }

  async getAutoDoerTask(taskId: string) {
    return this.request<AutoDoerTask>(`/autodoer/tasks/${encodeURIComponent(taskId)}`, {}, true);
  }

  async ingestAutoDoerTask(input: {
    source?: AutoDoerSource;
    title?: string;
    content?: string;
    metadata?: Record<string, unknown>;
    autoExecute?: boolean;
    dryRun?: boolean;
    [key: string]: unknown;
  }) {
    const source = (input.source || 'manual').trim();
    return this.request<AutoDoerTask>(
      `/autodoer/ingest/${encodeURIComponent(source)}`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }
//AutoDoer task approval and execution
  async approveAutoDoerTask(
    taskId: string,
    input: {
      decision: 'approved' | 'rejected';
      note?: string;
      autoRunAfterApproval?: boolean;
      dryRun?: boolean;
    }
  ) {
    return this.request<AutoDoerTask>(
      `/autodoer/tasks/${encodeURIComponent(taskId)}/approval`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async executeAutoDoerTask(
    taskId: string,
    input?: {
      dryRun?: boolean;
      force?: boolean;
    }
  ) {
    return this.request<AutoDoerTask>(
      `/autodoer/tasks/${encodeURIComponent(taskId)}/execute`,
      {
        method: 'POST',
        body: JSON.stringify(input || {}),
      },
      true
    );
  }

  // ─── AI Generation (App Builder) ──────────────────────────────────
  async generateAppConcept(prompt: string) {
    return this.request<{
      appName: string;
      tagline: string;
      features: string[];
      suggestedColorPalette: string[];
      logicWorkflow: string;
      techStack: string[];
      targetAudience: string;
      monetization: string;
      mvpScope: string[];
      competitiveEdge: string;
      userPersonas: { name: string; role: string; painPoint: string }[];
    }>(
      '/ai/generate',
      {
        method: 'POST',
        body: JSON.stringify({
          prompt: `Design a comprehensive, professional AI-powered app concept based on this request: "${prompt}".\n\nProvide ALL of the following:\n1. appName — a catchy product name\n2. tagline — a punchy one-liner\n3. features — 6-8 core features (each a clear sentence)\n4. suggestedColorPalette — 5 hex color codes that form a cohesive brand palette\n5. logicWorkflow — a detailed paragraph explaining the AI/logic architecture\n6. techStack — 4-6 recommended technologies\n7. targetAudience — one sentence describing who this is built for\n8. monetization — a suggested business model / pricing strategy in 1-2 sentences\n9. mvpScope — 3-4 items describing what to build in the first MVP release\n10. competitiveEdge — what makes this stand out from competitors in 1-2 sentences\n11. userPersonas — exactly 3 user personas, each with a name, role, and painPoint`,
          schema: {
            type: 'object',
            properties: {
              appName: { type: 'string' },
              tagline: { type: 'string' },
              features: { type: 'array', items: { type: 'string' } },
              suggestedColorPalette: { type: 'array', items: { type: 'string' } },
              logicWorkflow: { type: 'string' },
              techStack: { type: 'array', items: { type: 'string' } },
              targetAudience: { type: 'string' },
              monetization: { type: 'string' },
              mvpScope: { type: 'array', items: { type: 'string' } },
              competitiveEdge: { type: 'string' },
              userPersonas: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    name: { type: 'string' },
                    role: { type: 'string' },
                    painPoint: { type: 'string' },
                  },
                  required: ['name', 'role', 'painPoint'],
                },
              },
            },
            required: ['appName', 'tagline', 'features', 'suggestedColorPalette', 'logicWorkflow', 'techStack', 'targetAudience', 'monetization', 'mvpScope', 'competitiveEdge', 'userPersonas'],
          },
        }),
      },
      true,
      false,
      this.aiRequestTimeoutMs
    );
  }

  async streamAI(
    prompt: string,
    onChunk: (text: string) => void,
    context?: string,
    signal?: AbortSignal,
    images?: string[],
    onMeta?: (meta: Record<string, unknown>) => void,
    options?: { webSearch?: boolean; maxTokens?: number; onWebProgress?: (progress: { source: string; status: string; message: string }) => void }
  ): Promise<void> {
    const token = this.getToken();
    const response = await fetch(`${API_BASE_URL}/ai/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        prompt,
        context,
        images,
        webSearch: options?.webSearch,
        maxTokens: options?.maxTokens,
      }),
      signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`Stream request failed (${response.status})`);
    }

    await streamTextDeltas(response, onChunk, onMeta, options?.onWebProgress);
  }

  async getAIStatus() {
    return this.request<{
      providers: Record<string, boolean>;
      defaultProvider: string;
      ready: boolean;
      mode: 'live' | 'demo';
    }>('/ai/status', {}, true);
  }

  // Health Check
  async checkHealth() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5_000);
    try {
      const response = await fetch(`${API_BASE_URL.replace('/api', '')}/health`, {
        signal: controller.signal,
      });
      return await response.json();
    } catch (error) {
      return { status: 'unhealthy', error: 'Connection failed' };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Dashboard Sync — Fetch latest snapshot from IDE extension
  async getDashboardSync() {
    return this.request<{
      snapshot: {
        workspaceName?: string;
        activeFile?: string | null;
        diagnostics?: { errors?: number; warnings?: number };
        terminalOpen?: boolean;
        sidebarActive?: boolean;
        syncedAt?: string;
        editor?: string;
        projectStructure?: {
          root?: string;
          entries?: string[];
          totalEntries?: number;
          truncated?: boolean;
          maxDepth?: number;
          generatedAt?: string;
        };
      } | null;
      connected: boolean;
    }>('/dashboard/sync', {}, true);
  }

  // ── Self-Learning System ──────────────────────────────────

  /** Submit feedback (thumbs up/down/correction) for an AI response */
  async submitFeedback(data: {
    domain: string;
    prompt: string;
    response: string;
    rating: 'positive' | 'negative' | 'correction';
    correction?: string;
  }) {
    return this.request<{ feedbackId: string; patternsUpdated: number }>(
      '/learning/feedback',
      { method: 'POST', body: JSON.stringify(data) },
      true
    );
  }

  /** Get the intelligence score (0-100) for the current user */
  async getIntelligenceScore() {
    return this.request<{
      userId: string;
      score: number;
      totalInteractions: number;
      totalFeedbacks: number;
      positiveRate: number;
      topPatterns: string[];
      topPreferences: Record<string, string>;
      domainScores: Record<string, number>;
      memoryDepth: number;
      firstLearnedAt?: string;
      lastActiveAt?: string;
      activeDays30: number;
    }>('/learning/score', {}, true);
  }

  /** Get detailed learning stats */
  async getLearningStats() {
    return this.request<{
      totalInteractions: number;
      totalFeedbacks: number;
      totalPatterns: number;
      positiveRate: number;
      domainBreakdown: Record<string, { interactions: number; feedbacks: number; positiveRate: number }>;
      recentTrend: Array<{ date: string; interactions: number; feedbacks: number }>;
      avgLatencyMs: number;
      firstLearnedAt?: string | null;
      lastActiveAt?: string | null;
      activeDays30?: number;
    }>('/learning/stats', {}, true);
  }

  /** Get learned patterns */
  async getLearningPatterns() {
    return this.request<{
      userPatterns: Array<{ id: string; domain: string; pattern: string; confidence: number; sampleCount: number }>;
      globalPatterns: Array<{ id: string; domain: string; pattern: string; confidence: number; sampleCount: number }>;
    }>('/learning/patterns', {}, true);
  }

  /** Trigger pattern extraction for a domain */
  async extractPatterns(domain: string) {
    return this.request<{ extracted: number }>(
      '/learning/extract',
      { method: 'POST', body: JSON.stringify({ domain }) },
      true
    );
  }

  /** Preview what learning context would be injected for a domain */
  async getLearningContext(domain: string) {
    return this.request<{ context: string; patternCount: number; preferenceCount: number }>(
      `/learning/context/${encodeURIComponent(domain)}`,
      {},
      true
    );
  }

  /** Get recent autonomous agent run history */
  async getAgentRunHistory(limit = 20) {
    const boundedLimit = Math.max(1, Math.min(100, Math.floor(limit || 20)));
    return this.request<
      Array<{
        id: string;
        goal: string;
        planName: string;
        status: 'completed' | 'failed' | 'running';
        startedAt: string;
        completedAt?: string;
        totalDurationMs?: number;
        summary?: string;
        steps: Array<{
          order: number;
          label: string;
          intentKey: string;
          status: 'completed' | 'failed' | 'running';
          durationMs?: number;
        }>;
      }>
    >(`/agent/history?limit=${boundedLimit}`, {}, true);
  }

  /** Get saved decision memories from repository intelligence */
  async getDecisionMemories(limit = 25) {
    const boundedLimit = Math.max(1, Math.min(100, Math.floor(limit || 25)));
    return this.request<{
      count: number;
      entries: Array<{
        id: string;
        title: string;
        decision: string;
        context?: string;
        affectedFiles: string[];
        createdAt: string;
      }>;
    }>(`/github/repositories/decisions?limit=${boundedLimit}`, {}, true);
  }

  /** Generate architecture blueprint diagrams from connected repository data */
  async generateRepositoryBlueprint(input?: {
    focus?: string;
    maxModules?: number;
    maxEdges?: number;
  }) {
    return this.request<{
      repository: {
        fullName: string;
        branch: string;
        indexedAt: string;
      };
      generatedAt: string;
      focus?: string;
      summary: {
        totalFiles: number;
        codeFiles: number;
        testFiles: number;
        totalLines: number;
        modules: number;
        dependencies: number;
        couplingRisk: 'low' | 'medium' | 'high';
        testCoverageRatio: number;
      };
      modules: Array<{
        name: string;
        files: number;
        testFiles: number;
        lines: number;
        imports: number;
        share: number;
        inboundDependencies: number;
        outboundDependencies: number;
      }>;
      dependencies: Array<{
        from: string;
        to: string;
        count: number;
      }>;
      hotspots: Array<{
        path: string;
        module: string;
        imports: number;
        lines: number;
        testFile: boolean;
      }>;
      recommendations: string[];
      diagrams: {
        system: string;
        dependencies: string;
        hotspots: string;
      };
      markdown: string;
    }>(
      '/github/repositories/blueprint',
      {
        method: 'POST',
        body: JSON.stringify({
          focus: input?.focus,
          maxModules: input?.maxModules,
          maxEdges: input?.maxEdges,
        }),
      },
      true
    );
  }

  /** Search and filter Luma decision memory entries (Bet 3) */
  async getLumaDecisionMemory(input?: {
    limit?: number;
    q?: string;
    outcome?: 'pending' | 'validated' | 'rolled-back' | 'superseded';
  }) {
    const limit = Math.max(1, Math.min(200, Math.floor(input?.limit || 40)));
    const searchParams = new URLSearchParams();
    searchParams.set('limit', String(limit));
    if (input?.q && input.q.trim()) {
      searchParams.set('q', input.q.trim());
    }
    if (input?.outcome) {
      searchParams.set('outcome', input.outcome);
    }

    return this.request<{
      count: number;
      outcomeBreakdown: Record<'pending' | 'validated' | 'rolled-back' | 'superseded', number>;
      entries: Array<{
        id: string;
        title: string;
        decision: string;
        context?: string;
        affectedFiles: string[];
        outcomeStatus?: 'pending' | 'validated' | 'rolled-back' | 'superseded';
        impactScore?: number;
        adrId?: string;
        tags?: string[];
        lastReviewedAt?: string;
        createdAt: string;
      }>;
    }>(`/luma/decision-memory?${searchParams.toString()}`, {}, true);
  }

  /** Save decision memory entry with ADR/outcome metadata (Bet 3) */
  async saveLumaDecisionMemory(input: {
    title: string;
    decision: string;
    context?: string;
    affectedFiles?: string[];
    outcomeStatus?: 'pending' | 'validated' | 'rolled-back' | 'superseded';
    impactScore?: number;
    adrId?: string;
    tags?: string[];
  }) {
    return this.request<{
      entry: {
        id: string;
        title: string;
        decision: string;
        context?: string;
        affectedFiles: string[];
        outcomeStatus?: 'pending' | 'validated' | 'rolled-back' | 'superseded';
        impactScore?: number;
        adrId?: string;
        tags?: string[];
        lastReviewedAt?: string;
        createdAt: string;
      };
    }>(
      '/luma/decision-memory',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  /** Track outcome + impact for an existing decision memory entry (Bet 3) */
  async updateLumaDecisionOutcome(
    decisionId: string,
    input: {
      outcomeStatus: 'pending' | 'validated' | 'rolled-back' | 'superseded';
      impactScore?: number;
      notes?: string;
    }
  ) {
    return this.request<{
      entry: {
        id: string;
        title: string;
        decision: string;
        context?: string;
        affectedFiles: string[];
        outcomeStatus?: 'pending' | 'validated' | 'rolled-back' | 'superseded';
        impactScore?: number;
        adrId?: string;
        tags?: string[];
        lastReviewedAt?: string;
        createdAt: string;
      };
    }>(
      `/luma/decision-memory/${encodeURIComponent(decisionId)}/outcome`,
      {
        method: 'PATCH',
        body: JSON.stringify(input),
      },
      true
    );
  }

  /** Decision-memory rollups and ADR intelligence (Bet 3) */
  async getLumaDecisionIntelligence() {
    return this.request<{
      generatedAt: string;
      totals: {
        decisions: number;
        adrLinked: number;
        pendingReview: number;
        averageImpactScore: number | null;
        outcomeSuccessRate: number | null;
        intelligenceConfidence: number;
      };
      outcomeBreakdown: Record<'pending' | 'validated' | 'rolled-back' | 'superseded', number>;
      topImpactedFiles: Array<{ label: string; count: number }>;
      topTags: Array<{ label: string; count: number }>;
      stalePending: Array<{ id: string; title: string; ageDays: number }>;
      recentOutcomes: Array<{
        id: string;
        title: string;
        outcomeStatus: 'pending' | 'validated' | 'rolled-back' | 'superseded';
        impactScore: number | null;
        reviewedAt: string;
      }>;
    }>('/luma/decision-intelligence', {}, true);
  }

  /** Predictive engineering risk snapshot before merge (Bet 4) */
  async getLumaPredictive(windowDays = 14) {
    const boundedWindow = Math.max(7, Math.min(45, Math.floor(windowDays || 14)));
    return this.request<{
      generatedAt: string;
      windowDays: number;
      forecastConfidence: number;
      metrics: {
        releaseRisk: { score: number; level: 'low' | 'medium' | 'high' | 'critical'; signals: number };
        incidentLikelihood: { score: number; level: 'low' | 'medium' | 'high' | 'critical'; signals: number };
        dependencyRisk: { score: number; level: 'low' | 'medium' | 'high' | 'critical'; signals: number };
        costOfDelay: { score: number; level: 'low' | 'medium' | 'high' | 'critical'; signals: number };
      };
      drivers: string[];
      recommendations: string[];
    }>(`/luma/predictive?windowDays=${boundedWindow}`, {}, true);
  }

  /** Real-time engineering command center snapshot (Bet 5) */
  async getLumaMissionControl(windowMinutes = 180) {
    const boundedWindow = Math.max(30, Math.min(720, Math.floor(windowMinutes || 180)));
    return this.request<{
      generatedAt: string;
      windowMinutes: number;
      pulse: {
        activityEvents: number;
        telemetryEvents: number;
        connectedEditors: number;
        intelligenceScore: number;
      };
      statusTiles: Array<{
        id: string;
        label: string;
        state: 'healthy' | 'watch' | 'critical';
        detail: string;
      }>;
      blockers: Array<{
        id: string;
        title: string;
        severity: 'medium' | 'high' | 'critical';
        source: string;
        createdAt: string;
      }>;
      activeRisks: Array<{
        metric: 'releaseRisk' | 'incidentLikelihood' | 'dependencyRisk';
        score: number;
        level: 'low' | 'medium' | 'high' | 'critical';
      }>;
      recommendations: string[];
      feed: Array<{
        id: string;
        source: 'code' | 'tickets' | 'docs' | 'incidents' | 'meetings' | 'decisions';
        title: string;
        timestamp: string;
        status: string;
        detail?: string;
      }>;
    }>(`/luma/mission-control?windowMinutes=${boundedWindow}`, {}, true);
  }

  /** Get deep Luma Brain OS snapshot across product sources */
  async getLumaBrainSnapshot(rangeDays = 21) {
    const boundedRange = Math.max(7, Math.min(60, Math.floor(rangeDays || 21)));
    return this.request<{
      generatedAt: string;
      summary: {
        coverageScore: number;
        sourcesActive: number;
        totalSignals: number;
        feedbackCount: number;
        decisionCount: number;
      };
      intelligenceScore: {
        userId: string;
        score: number;
        totalInteractions: number;
        totalFeedbacks: number;
        positiveRate: number;
        topPatterns: string[];
        topPreferences: Record<string, string>;
        domainScores: Record<string, number>;
        memoryDepth: number;
        firstLearnedAt?: string;
        lastActiveAt?: string;
        activeDays30: number;
      };
      learningStats: {
        totalInteractions: number;
        totalFeedbacks: number;
        totalPatterns: number;
        positiveRate: number;
        domainBreakdown: Record<string, { interactions: number; feedbacks: number; positiveRate: number }>;
        recentTrend: Array<{ date: string; interactions: number; feedbacks: number }>;
        avgLatencyMs: number;
      };
      sourceSignals: Array<{
        source: 'code' | 'tickets' | 'docs' | 'incidents' | 'meetings' | 'decisions';
        label: string;
        confidence: number;
        signals: number;
        feedbacks: number;
        positiveRate: number;
        last7Days: number;
        trend: number;
        detail: string;
      }>;
      dailyHistory: Array<{
        date: string;
        total: number;
        interactions: number;
        feedbacks: number;
        sources: Record<'code' | 'tickets' | 'docs' | 'incidents' | 'meetings' | 'decisions', number>;
      }>;
      timeline: Array<{
        id: string;
        source: 'code' | 'tickets' | 'docs' | 'incidents' | 'meetings' | 'decisions';
        title: string;
        timestamp: string;
        status: string;
        detail?: string;
      }>;
      decisions: Array<{
        id: string;
        title: string;
        decision: string;
        context?: string;
        affectedFiles: string[];
        createdAt: string;
      }>;
    }>(`/luma/brain?rangeDays=${boundedRange}`, {}, true);
  }

  /**
   * Intelligence panel snapshot — combines all 8 data sources into 1 request.
   * Use this instead of calling getLumaBrainSnapshot + getLearningPatterns +
   * getDashboardSync + getAgentRunHistory + getLumaDecisionMemory +
   * getLumaDecisionIntelligence separately.
   */
  async getIntelligenceSnapshot() {
    return this.request<{
      brain: {
        generatedAt: string;
        intelligenceScore: Record<string, unknown>;
        learningStats: Record<string, unknown>;
        decisions: Array<Record<string, unknown>>;
        summary: { coverageScore: number; sourcesActive: number; totalSignals: number; feedbackCount: number; decisionCount: number };
      } | null;
      patterns: {
        userPatterns: Array<{ id: string; domain: string; pattern: string; confidence: number; sampleCount: number }>;
        globalPatterns: Array<{ id: string; domain: string; pattern: string; confidence: number; sampleCount: number }>;
      } | null;
      decisionMemory: Array<Record<string, unknown>> | null;
      decisionIntelligence: Record<string, unknown> | null;
      dashboardSync: { snapshot: Record<string, unknown> | null; connected: boolean };
      agentRuns: Array<Record<string, unknown>>;
    }>('/luma/intelligence-snapshot', {}, true);
  }

  async resolveLumiDiscovery(input: { query: string }) {
    return this.request<{
      kind: 'none' | 'database' | 'templates';
      query: string;
      generatedAt: string;
      cacheHit: boolean;
      company?: string;
      category?: string;
      databaseData?: Record<string, unknown>;
      templateData?: Record<string, unknown>;
    }>(
      '/lumi/discovery/resolve',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      false,
    );
  }

  async extractLumiDocument(file: File): Promise<ApiResponse<{ name: string; text: string; charCount: number }>> {
    const token = this.getToken();
    const formData = new FormData();
    formData.append('document', file);

    const response = await fetch(`${API_BASE_URL}/lumi/extract-document`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    const body = (await response.json()) as ApiResponse<{ name: string; text: string; charCount: number }>;
    return body;
  }

  async getLumiVoiceToken(): Promise<ApiResponse<{ secret: string; expiresAt?: number }>> {
    return this.request<{ secret: string; expiresAt?: number }>('/lumi/voice-token', { method: 'GET' }, true);
  }

  async synthesizeLumiSpeech(input: { text: string; languageCode?: string; pace?: number }) {
    return this.request<{ audioBase64: string; mimeType: string; provider: 'sarvam' }>(
      '/lumi/voice/synthesize',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  }

  async continueLumiQuery(input: { continuationToken: string }) {
    return this.request<{
      kind: 'answer';
      answerData: {
        answer: string;
        sources: Array<{ title: string; domain: string; snippet: string }>;
        followUps: string[];
        hasMore?: boolean;
        continuationToken?: string;
      };
    }>(
      '/lumi/query/continue',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      false,
      false,
      this.aiRequestTimeoutMs,
    );
  }

  async resolveLumiQuery(input: {
    query: string;
    workspaceMode?: string;
    displayName?: string;
    conversationId?: string;
    repositoryContext?: {
      fullName: string;
      branch?: string;
      fileTree?: string[];
      stats?: {
        files?: number;
        lines?: number;
        languages?: Array<{ name: string; value: number }>;
        modules?: Array<{ name: string; value: number }>;
        hotspots?: Array<{ path: string; imports: number }>;
      };
    };
    documentContext?: {
      name: string;
      text: string;
    };
    persona?: string;
    journalNotes?: Array<{
      tag: string;
      content: string;
    }>;
    recentMessages?: Array<{
      role: 'user' | 'lumi';
      content: string;
      mode?: string;
      createdAt?: string;
    }>;
    images?: string[];
    pdfPages?: Array<{ pageNumber: number; imageUrl?: string; title?: string }>;
  }, options?: { signal?: AbortSignal }) {
    return this.request<{
      kind: 'database' | 'templates' | 'answer';
      query: string;
      generatedAt: string;
      cacheHit: boolean;
      company?: string;
      category?: string;
      databaseData?: Record<string, unknown>;
      templateData?: Record<string, unknown>;
      answerData?: {
        answer: string;
        sources: Array<{ title: string; domain: string; snippet: string }>;
        followUps: string[];
        presentation?: 'CHAT' | 'WHITE_SHEET' | 'TABLE' | 'CODE_EDITOR' | 'DOCUMENT_VIEW' | 'DATA_TABLE' | 'CHART' | 'FLOWCHART' | 'FILE_ANALYSIS' | 'RESEARCH_VIEW' | 'INTERACTIVE_QUIZ';
        task?: string;
        hasMore?: boolean;
        continuationToken?: string;
      };
    }>(
      '/lumi/query',
      {
        method: 'POST',
        body: JSON.stringify(input),
        signal: options?.signal,
      },
      true,
      false,
      this.aiRequestTimeoutMs,
    );
  }

  async resolveLumiQueryStreaming(
    input: {
      query: string;
      workspaceMode?: string;
      displayName?: string;
      conversationId?: string;
      persona?: string;
      journalNotes?: Array<{
        tag: string;
        content: string;
      }>;
      repositoryContext?: {
        fullName: string;
        branch?: string;
        fileTree?: string[];
        stats?: {
          files?: number;
          lines?: number;
          languages?: Array<{ name: string; value: number }>;
          modules?: Array<{ name: string; value: number }>;
          hotspots?: Array<{ path: string; imports: number }>;
        };
      };
      documentContext?: {
        name: string;
        text: string;
      };
      recentMessages?: Array<{
        role: 'user' | 'lumi';
        content: string;
        mode?: string;
        createdAt?: string;
      }>;
      images?: string[];
      pdfPages?: Array<{ pageNumber: number; imageUrl?: string; title?: string }>;
    },
    onProgress: (progress: { source: string; status: string; message: string; results?: unknown[]; error?: string }) => void,
  ) {
    const token = this.getToken();
    const url = `${API_BASE_URL}/lumi/query/stream`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      throw new Error(`Lumi stream failed: ${response.status}`);
    }

    let finalData: unknown = null;

    for await (const { event, data } of readSSEEvents(response)) {
      if (event === 'progress') {
        onProgress(data as { source: string; status: string; message: string; results?: unknown[]; error?: string });
      } else {
        finalData = data;
      }
    }

    return finalData;
  }

  async geocodePlace(place: string) {
    return this.request<{
      lat: number;
      lon: number;
      label: string;
      source: string;
    }>(`/maps/geocode?q=${encodeURIComponent(place)}`, {}, false);
  }

  async getConnectedGithubRepository() {
    return this.request<{
      connectedRepo: {
        fullName: string;
        branch: string;
        clonePath: string;
        indexedAt: string;
        fileTree?: string[];
        stats?: {
          files: number;
          testFiles: number;
          lines: number;
          languages: Array<{ name: string; value: number }>;
          modules: Array<{ name: string; value: number }>;
          hotspots: Array<{ path: string; imports: number }>;
        };
      } | null;
    }>('/github/repositories/connected', {}, true);
  }

  async getGitHubProfile(username: string) {
    return this.request<{
      user: {
        id: number | null;
        login: string;
        name: string | null;
        avatar_url: string | null;
        html_url: string | null;
        bio: string | null;
        followers: number;
        following: number;
        public_repos: number;
        company: string | null;
        location: string | null;
        blog: string | null;
      };
      repos: Array<{
        id: number | null;
        name: string;
        full_name: string;
        description: string;
        html_url: string;
        language: string | null;
        stargazers_count: number;
        forks_count: number;
        updated_at: string;
        private: boolean;
      }>;
    }>(`/github/profile/${encodeURIComponent(username)}`, {}, true);
  }

  async getLumiWorkspaceState() {
    return this.request<{
      sessions: Array<{
        id: string;
        title: string;
        detail: string;
        mode: 'general' | 'database' | 'design' | 'team' | 'build' | 'strategy';
        startedAt: string;
        updatedAt: string;
      }>;
      messages: Array<{
        id: string;
        conversationId: string;
        role: 'user' | 'lumi';
        content: string;
        query?: string;
        mode: 'general' | 'database' | 'design' | 'team' | 'build' | 'strategy';
        status?: 'thinking' | 'streaming' | 'complete' | 'error';
        sources?: Array<{ title: string; domain: string; snippet: string }>;
        followUps?: string[];
        templateShowcase?: {
          category: string;
          headline?: string;
          insight?: string;
          templates: Array<{
            title: string;
            description: string;
            source: string;
            sourceDomain: string;
            previewUrl: string;
            imageUrl: string;
            tags: string[];
            rating: string;
            style: string;
            price: string;
          }>;
        };
        createdAt: string;
      }>;
      memories: Array<{
        id: string;
        title: string;
        detail: string;
        mode: 'general' | 'database' | 'design' | 'team' | 'build' | 'strategy';
        updatedAt: string;
        pinned?: boolean;
        conversationId?: string;
      }>;
      activeConversationId: string | null;
      updatedAt: string;
    }>('/lumi/workspace', {}, true);
  }

  async getLumiConversation(conversationId: string) {
    return this.request<{ conversation: { id: string; title: string; detail: string; mode: 'general' | 'database' | 'design' | 'team' | 'build' | 'strategy'; startedAt: string; updatedAt: string } }>(`/lumi/conversations/${encodeURIComponent(conversationId)}`, {}, true);
  }

  async getLumiConversationMessages(conversationId: string, options?: { before?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.before) params.set('before', options.before);
    if (options?.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    return this.request<{ messages: Array<Record<string, unknown>>; hasMore: boolean; nextBefore?: string }>(`/lumi/conversations/${encodeURIComponent(conversationId)}/messages${query ? `?${query}` : ''}`, {}, true);
  }

  async getLumiConversationFiles(conversationId: string) {
    return this.request<{ files: Array<{ name: string; charCount: number; numPages?: number; attachedAt: string }> }>(`/lumi/conversations/${encodeURIComponent(conversationId)}/files`, {}, true);
  }

  async saveLumiWorkspaceState(input: {
    sessions: Array<{
      id: string;
      title: string;
      detail: string;
      mode: 'general' | 'database' | 'design' | 'team' | 'build' | 'strategy';
      startedAt: string;
      updatedAt: string;
    }>;
    messages: Array<{
      id: string;
      conversationId: string;
      role: 'user' | 'lumi';
      content: string;
      query?: string;
      mode: 'general' | 'database' | 'design' | 'team' | 'build' | 'strategy';
      status?: 'thinking' | 'streaming' | 'complete' | 'error';
      sources?: Array<{ title: string; domain: string; snippet: string }>;
      followUps?: string[];
      templateShowcase?: {
        category: string;
        headline?: string;
        insight?: string;
        templates: Array<{
          title: string;
          description: string;
          source: string;
          sourceDomain: string;
          previewUrl: string;
          imageUrl: string;
          tags: string[];
          rating: string;
          style: string;
          price: string;
        }>;
      };
      createdAt: string;
    }>;
    memories: Array<{
      id: string;
      title: string;
      detail: string;
      mode: 'general' | 'database' | 'design' | 'team' | 'build' | 'strategy';
      updatedAt: string;
      pinned?: boolean;
      conversationId?: string;
    }>;
    activeConversationId: string | null;
  }) {
    return this.request<{
      sessions: Array<{
        id: string;
        title: string;
        detail: string;
        mode: 'general' | 'database' | 'design' | 'team' | 'build' | 'strategy';
        startedAt: string;
        updatedAt: string;
      }>;
      messages: Array<{
        id: string;
        conversationId: string;
        role: 'user' | 'lumi';
        content: string;
        query?: string;
        mode: 'general' | 'database' | 'design' | 'team' | 'build' | 'strategy';
        status?: 'thinking' | 'streaming' | 'complete' | 'error';
        sources?: Array<{ title: string; domain: string; snippet: string }>;
        followUps?: string[];
        templateShowcase?: {
          category: string;
          headline?: string;
          insight?: string;
          templates: Array<{
            title: string;
            description: string;
            source: string;
            sourceDomain: string;
            previewUrl: string;
            imageUrl: string;
            tags: string[];
            rating: string;
            style: string;
            price: string;
          }>;
        };
        createdAt: string;
      }>;
      memories: Array<{
        id: string;
        title: string;
        detail: string;
        mode: 'general' | 'database' | 'design' | 'team' | 'build' | 'strategy';
        updatedAt: string;
        pinned?: boolean;
        conversationId?: string;
      }>;
      activeConversationId: string | null;
      updatedAt: string;
    }>(
      '/lumi/workspace',
      {
        method: 'PUT',
        body: JSON.stringify(input),
      },
      true,
    );
  }

  /** Generate server-side why-change brief from Luma Brain intelligence */
  async generateLumaWhyChange(input: {
    domain: string;
    changeSummary: string;
    impactScope?: string;
    changedFiles?: string[];
  }) {
    return this.request<{
      riskScore: number;
      riskLevel: 'low' | 'medium' | 'high' | 'critical';
      rationale: string[];
      checklist: string[];
      relatedDecisions: Array<{
        id: string;
        title: string;
        decision: string;
        context?: string;
        affectedFiles: string[];
        createdAt: string;
      }>;
      learnedContext: string;
    }>(
      '/luma/why-change',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  // Trust Layer
  async getTrustLayerState() {
    return this.request<TrustLayerState>('/trust-layer/state', {}, true);
  }

  async createTrustProject(input: { name: string; founderBrief: string }) {
    return this.request<TrustProject>(
      '/trust-layer/projects',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async setTrustSpecLock(
    projectId: string,
    input: {
      title: string;
      objective: string;
      inScope: string[];
      outOfScope?: string[];
      timelineDays?: number;
      budgetInr?: number;
    }
  ) {
    return this.request<TrustProject>(
      `/trust-layer/projects/${encodeURIComponent(projectId)}/spec-lock`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async setTrustAcceptanceContract(
    projectId: string,
    input: {
      criteria: Array<{ text: string; metric?: string }>;
    }
  ) {
    return this.request<TrustAcceptanceContract>(
      `/trust-layer/projects/${encodeURIComponent(projectId)}/acceptance`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async addTrustChangeRequest(
    projectId: string,
    input: {
      summary: string;
      reason?: string;
      approvedBy?: string;
      status?: TrustChangeRequestStatus;
      impact?: TrustChangeImpact;
    }
  ) {
    return this.request<TrustChangeRequestEntry>(
      `/trust-layer/projects/${encodeURIComponent(projectId)}/change-requests`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async getTrustReport(projectId: string) {
    return this.request<TrustReport>(
      `/trust-layer/projects/${encodeURIComponent(projectId)}/trust-report`,
      {},
      true
    );
  }

  async getTrustIntelligence(projectId: string) {
    return this.request<TrustIntelligenceReport>(
      `/trust-layer/projects/${encodeURIComponent(projectId)}/intelligence`,
      {},
      true
    );
  }

  async getTrustScorecard(projectId: string) {
    return this.request<TrustScorecard>(
      `/trust-layer/projects/${encodeURIComponent(projectId)}/scorecard`,
      {},
      true
    );
  }

  async getTrustPostMortem(projectId: string) {
    return this.request<TrustPostMortem>(
      `/trust-layer/projects/${encodeURIComponent(projectId)}/post-mortem`,
      {},
      true
    );
  }

  async getTrustInvestorReport(projectId: string) {
    return this.request<TrustInvestorReport>(
      `/trust-layer/projects/${encodeURIComponent(projectId)}/investor-report`,
      {},
      true
    );
  }

  // Reminders
  async listReminders() {
    return this.request<{
      reminders: Array<{
        id: string;
        title: string;
        detail: string;
        source: 'voice' | 'chat' | 'extension' | 'manual';
        status: 'pending' | 'done' | 'dismissed';
        createdAt: string;
        updatedAt: string;
        remindAt?: string;
        snoozeUntil?: string;
        recurrence?: string;
        lastNotifiedAt?: string;
        originUrl?: string;
      }>;
      dismissedAt?: string;
      updatedAt: string;
    }>('/reminders', {}, true);
  }

  async createReminder(input: {
    title: string;
    detail?: string;
    source?: 'voice' | 'chat' | 'extension' | 'manual';
    remindAt?: string;
    snoozeUntil?: string;
    recurrence?: string;
    originUrl?: string;
  }) {
    return this.request<{
      id: string;
      title: string;
      detail: string;
      source: 'voice' | 'chat' | 'extension' | 'manual';
      status: 'pending' | 'done' | 'dismissed';
      createdAt: string;
      updatedAt: string;
      remindAt?: string;
      snoozeUntil?: string;
      recurrence?: string;
      originUrl?: string;
    }>(
      '/reminders',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true,
    );
  }

  async updateReminder(
    id: string,
    input: {
      title?: string;
      detail?: string;
      status?: 'pending' | 'done' | 'dismissed';
      remindAt?: string;
      snoozeUntil?: string;
      recurrence?: string;
      originUrl?: string;
    },
  ) {
    return this.request<{
      id: string;
      title: string;
      detail: string;
      source: 'voice' | 'chat' | 'extension' | 'manual';
      status: 'pending' | 'done' | 'dismissed';
      createdAt: string;
      updatedAt: string;
      remindAt?: string;
      snoozeUntil?: string;
      recurrence?: string;
      originUrl?: string;
    }>(`/reminders/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(input) }, true);
  }

  async snoozeReminder(id: string, until: string) {
    return this.request<{ id: string }>(
      `/reminders/${encodeURIComponent(id)}/snooze`,
      { method: 'POST', body: JSON.stringify({ until }) },
      true,
    );
  }

  async dismissReminder(id: string) {
    return this.request<{ id: string }>(
      `/reminders/${encodeURIComponent(id)}/dismiss`,
      { method: 'POST' },
      true,
    );
  }

  async deleteReminder(id: string) {
    return this.request<{ id: string }>(`/reminders/${encodeURIComponent(id)}`, { method: 'DELETE' }, true);
  }

  async dismissAllReminders() {
    return this.request<{
      reminders: Array<{
        id: string;
        title: string;
        detail: string;
        source: 'voice' | 'chat' | 'extension' | 'manual';
        status: 'pending' | 'done' | 'dismissed';
        createdAt: string;
        updatedAt: string;
        remindAt?: string;
        snoozeUntil?: string;
        recurrence?: string;
        originUrl?: string;
      }>;
      dismissedAt?: string;
      updatedAt: string;
    }>('/reminders/dismiss-all', { method: 'POST' }, true);
  }

  // Media generation
  async generateMedia(input: {
    prompt: string;
    type: 'image' | 'video' | '3d';
    aspectRatio?: string;
    style?: string;
    negativePrompt?: string;
  }) {
    return this.request<{
      id: string;
      url: string;
      type: 'image' | 'video' | '3d';
      prompt: string;
      enhancedPrompt?: string;
      provider: string;
      createdAt: string;
    }>('/media/generate', { method: 'POST', body: JSON.stringify(input) }, true);
  }

  async listMediaHistory() {
    return this.request<
      Array<{
        id: string;
        url: string;
        type: 'image' | 'video' | '3d';
        prompt: string;
        enhancedPrompt?: string;
        provider: string;
        createdAt: string;
      }>
    >('/media/history', {}, true);
  }

  async saveMediaEdit(body: { originalId?: string; type?: string; url: string; prompt?: string; edits?: string }) {
    return this.request<{ id: string }>('/media/save-edit', { method: 'POST', body: JSON.stringify(body) }, true);
  }

  // SEO / Repository Intelligence
  async analyzeSEO(url: string) {
    return this.request<{
      url: string;
      psi: Record<string, unknown>;
      meta: Record<string, unknown>;
      robots: Record<string, unknown>;
    }>(`/seo/analyze?url=${encodeURIComponent(url)}`, {}, false);
  }

  async scanRepositoryIntelligence() {
    return this.request<{
      findings: Array<{
        id: string;
        category: 'license' | 'privacy' | 'security' | 'ip' | 'dependency' | 'sovereignty';
        severity: 'critical' | 'high' | 'medium' | 'low';
        title: string;
        files: string[];
        lineRefs: string[];
        explanation: string;
        consequences: string;
        recommendation: string;
        fixSuggestion: string;
      }>;
      scannedAt: string;
    }>('/repo/intelligence/scan', {}, true);
  }

  // Push notifications
  async getVapidPublicKey() {
    return this.request<{ publicKey?: string; configured: boolean }>('/notifications/push/vapid-public-key', {}, false);
  }

  async subscribePush(subscription: PushSubscriptionJSON) {
    return this.request<{ success: boolean }>('/notifications/push/subscribe', { method: 'POST', body: JSON.stringify(subscription) }, true);
  }

  async unsubscribePush(endpoint: string) {
    return this.request<{ success: boolean }>(`/notifications/push/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`, { method: 'DELETE' }, true);
  }

  // GitHub publishing
  async getGithubStatus() {
    return this.request<{
      connected: boolean;
      connectedAt: string | null;
      login: string | null;
      hasToken: boolean;
      connectedRepo: unknown;
    }>('/github/status', {}, true);
  }

  async getGitHubConnectUrl(returnTo = '/studio?ship=github') {
    const params = new URLSearchParams();
    params.set('returnTo', returnTo);
    return this.request<{ provider: string; url: string }>(
      `/auth/oauth/github/connect/start?${params.toString()}`,
      {},
      true
    );
  }

  async pushGitHubRepo(input: {
    repoName: string;
    description?: string;
    files: Array<{ path: string; content: string }>;
    isPrivate?: boolean;
  }) {
    return this.request<{ url: string }>(
      '/github/push-repo',
      {
        method: 'POST',
        body: JSON.stringify({
          repoName: input.repoName,
          description: input.description,
          files: input.files,
          isPrivate: input.isPrivate !== false,
        }),
      },
      true
    );
  }

  async fixFindingWithPR(input: {
    file: string;
    instruction: string;
    title?: string;
    branch?: string;
    /** Autopilot gate: skip PR creation when computed confidence is below this. */
    minConfidence?: number;
  }) {
    return this.request<{
      url?: string;
      branch?: string;
      number?: number;
      confidence?: number;
      skipped?: boolean;
      testIncluded?: boolean;
      testPath?: string;
      appliedIn?: number;
    }>(
      '/github/repositories/fix-pr',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async applyLocalFix(input: { file: string; code?: string; find?: string; replace?: string }) {
    return this.request<{ path: string; updated?: boolean; written?: boolean; created?: boolean; verified?: boolean }>(
      '/github/repositories/apply-local-fix',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async attributeFindings(files: string[], since?: string) {
    return this.request<{
      repo: string;
      since: string;
      attributions: Array<{
        file: string;
        commits: Array<{ sha: string; message: string; author: string; date: string; url?: string }>;
      }>;
    }>(
      '/github/intel/repositories/attribute',
      { method: 'POST', body: JSON.stringify({ files, since }) },
      true
    );
  }

  async queryRepo(question: string) {
    return this.request<{ answer: string; citations: string[]; repo: string }>(
      '/github/intel/repositories/query',
      { method: 'POST', body: JSON.stringify({ question }) },
      true
    );
  }

  async publishRepoFingerprint(repo: string, fingerprint: { stack: string[]; dna: Array<{ label: string; value: number }>; primaryLanguage: string }) {
    return this.request<{ published: boolean }>(
      '/github/intel/repositories/twins',
      { method: 'POST', body: JSON.stringify({ repo, fingerprint }) },
      true
    );
  }

  async getRepoTwins(repo: string) {
    return this.request<{ twins: Array<{ repo: string; similarity: number }> }>(
      `/github/intel/repositories/twins?repo=${encodeURIComponent(repo)}`,
      {},
      true
    );
  }

  async createIssueFromFinding(input: { title: string; body: string; labels?: string[] }) {
    return this.request<{ url: string; number: number }>(
      '/github/repositories/create-issue',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true
    );
  }

  async deployGitHubPages(input: {
    repoName: string;
    description?: string;
    files: Array<{ path: string; content: string }>;
    isPrivate?: boolean;
  }) {
    return this.request<{ repoUrl: string; pagesUrl: string }>(
      '/github/deploy-pages',
      {
        method: 'POST',
        body: JSON.stringify({
          repoName: input.repoName,
          description: input.description,
          files: input.files,
          isPrivate: input.isPrivate !== false,
        }),
      },
      true
    );
  }

  generateImageUrl(prompt: string, width = 1024, height = 1024) {
    const encoded = encodeURIComponent(prompt);
    return `https://image.pollinations.ai/prompt/${encoded}?width=${width}&height=${height}&nologo=true&seed=${Math.floor(Math.random() * 1000000)}`;
  }

  // ─── Agent Swarm ────────────────────────────────────────────────────

  async planSwarm(prompt: string, projectType?: string) {
    return this.request<{ agents: any[]; tasks: any[]; estimatedTimeMs: number; parallelGroups: string[][] }>(
      '/v1/agent-swarm/plan',
      { method: 'POST', body: JSON.stringify({ prompt, projectType }) },
      true
    );
  }

  async createSwarmSession(prompt: string) {
    return this.request<any>(
      '/v1/agent-swarm/create',
      { method: 'POST', body: JSON.stringify({ prompt }) },
      true
    );
  }

  async executeSwarmTask(sessionId: string, taskId: string) {
    return this.request<any>(`/v1/agent-swarm/execute/${sessionId}/${taskId}`, { method: 'POST' }, true);
  }

  async executeFullSwarm(sessionId: string) {
    return this.request<any>(`/v1/agent-swarm/execute-all/${sessionId}`, { method: 'POST' }, true);
  }

  async getSwarmStatus(sessionId: string) {
    return this.request<any>(`/v1/agent-swarm/status/${sessionId}`, { method: 'GET' }, true);
  }

  async mergeSwarmOutputs(sessionId: string) {
    return this.request<{ mergedOutput: string }>(`/v1/agent-swarm/merge/${sessionId}`, { method: 'POST' }, true);
  }

  async cancelSwarm(sessionId: string) {
    return this.request<any>(`/v1/agent-swarm/${sessionId}`, { method: 'DELETE' }, true);
  }

  // ─── Usage Metering ─────────────────────────────────────────────────

  async getUsage() {
    return this.request<{ used: number; quota: number; percent: number; plan: string; month: string; overQuota: boolean }>(
      '/v1/usage/me',
      {},
      true,
    );
  }

  async incrementUsage(amount = 1) {
    return this.request<{ used: number; quota: number; percent: number; plan: string; month: string; overQuota: boolean }>(
      '/v1/usage/increment',
      { method: 'POST', body: JSON.stringify({ amount }) },
      true,
    );
  }

  // ─── Scrudix Brain ──────────────────────────────────────────────────

  async captureProject(data: { name: string; files: Array<{ path: string; content: string }>; techStack: string[] }) {
    return this.request<any>('/v1/scrudix-brain/capture', { method: 'POST', body: JSON.stringify(data) }, true);
  }

  async getBrainContext(userId: string, prompt: string) {
    return this.request<any>(`/v1/scrudix-brain/context/${userId}?prompt=${encodeURIComponent(prompt)}`, { method: 'GET' }, true);
  }

  async learnFromBuild(data: { projectId: string; files: Array<{ path: string; content: string }>; feedback?: { rating: number; comments?: string } }) {
    return this.request<any>('/v1/scrudix-brain/learn', { method: 'POST', body: JSON.stringify(data) }, true);
  }

  async getBrainDesignSystems(userId: string) {
    return this.request<any>(`/v1/scrudix-brain/design-systems/${userId}`, { method: 'GET' }, true);
  }

  async getBrainComponents(userId: string) {
    return this.request<any>(`/v1/scrudix-brain/components/${userId}`, { method: 'GET' }, true);
  }

  async getBrainSuggestions(userId: string, projectData: { name: string; files: Array<{ path: string; content: string }>; techStack: string[] }) {
    return this.request<any>(`/v1/scrudix-brain/suggestions/${userId}`, { method: 'POST', body: JSON.stringify(projectData) }, true);
  }

  async searchBrainMemory(userId: string, query: string) {
    return this.request<any>(`/v1/scrudix-brain/search/${userId}?q=${encodeURIComponent(query)}`, { method: 'GET' }, true);
  }

  // ─── Deploy Enhanced ────────────────────────────────────────────────

  async getDeployTargets() {
    return this.request<any>('/v1/deploy-enhanced/targets', { method: 'GET' }, true);
  }

  async autoConfigDeploy(files: Record<string, string>) {
    return this.request<any>('/v1/deploy-enhanced/auto-config', { method: 'POST', body: JSON.stringify({ files }) }, true);
  }

  async deployToPlatform(input: { files: Record<string, string>; platform: string; projectName: string; apiToken?: string }) {
    return this.request<any>('/v1/deploy-enhanced/deploy', { method: 'POST', body: JSON.stringify(input) }, true);
  }

  async getDeployStatus(deployId: string) {
    return this.request<any>(`/v1/deploy-enhanced/status/${deployId}`, { method: 'GET' }, true);
  }

  async generateCI(input: { files: Record<string, string>; platform: string }) {
    return this.request<any>('/v1/deploy-enhanced/generate-ci', { method: 'POST', body: JSON.stringify(input) }, true);
  }

  // ─── Community ──────────────────────────────────────────────────────
  async getCommunityFeed(userId?: string, filter?: string) {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (filter) params.set('filter', filter);
    const query = params.toString();
    return this.request<{ posts: CommunityPost[]; total: number; stats?: { membersCount: number; postsCount: number }; following?: string[] }>(
      `/community/feed${query ? `?${query}` : ''}`,
      {},
      true
    );
  }

  async toggleCommunityBookmark(postId: string) {
    return this.request<{ bookmarked: boolean }>(
      `/community/posts/${encodeURIComponent(postId)}/bookmark`,
      { method: 'POST' },
      true
    );
  }

  async getCommunityBookmarks() {
    return this.request<{ posts: CommunityPost[]; total: number }>(
      '/community/bookmarks',
      {},
      true
    );
  }

  async createCommunityPost(post: {
    title?: string;
    content: string;
    image?: string;
    linkPreview?: Record<string, unknown>;
    poll?: Record<string, unknown>;
    fontFamily?: string;
  }) {
    return this.request<{ post: CommunityPost }>('/community/posts', {
      method: 'POST',
      body: JSON.stringify(post),
    }, true);
  }

  async deleteCommunityPost(id: string) {
    return this.request<{ deleted: boolean }>(`/community/posts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }, true);
  }

  async voteCommunityPost(id: string, vote: -1 | 0 | 1) {
    return this.request<{ score: number; userVote: -1 | 0 | 1 }>(
      `/community/posts/${encodeURIComponent(id)}/vote`,
      { method: 'POST', body: JSON.stringify({ vote }) },
      true
    );
  }

  async reactToCommunityPost(id: string, emoji: string) {
    return this.request<{ reactions: Record<string, number>; active: boolean; userEmojis: string[] }>(
      `/community/posts/${encodeURIComponent(id)}/reaction`,
      { method: 'POST', body: JSON.stringify({ emoji }) },
      true
    );
  }

  async searchCommunityPosts(q: string, options?: { channel?: string; limit?: number }) {
    const params = new URLSearchParams({ q });
    if (options?.channel) params.set('channel', options.channel);
    if (options?.limit) params.set('limit', String(options.limit));
    return this.request<{ posts: CommunityPost[]; total: number; query: string }>(
      `/community/search?${params.toString()}`
    );
  }

  async reportCommunityPost(id: string, reason?: string) {
    return this.request<{ reported: boolean }>(
      `/community/posts/${encodeURIComponent(id)}/report`,
      { method: 'POST', body: JSON.stringify({ reason }) },
      true
    );
  }

  async blockCommunityUser(id: string) {
    return this.request<{ blocked: boolean }>(
      `/community/users/${encodeURIComponent(id)}/block`,
      { method: 'POST' },
      true
    );
  }

  async sendDirectMessage(id: string, content: string) {
    return this.request<{ message: { id: string; senderId: string; recipientId: string; content: string; createdAt: string } }>(
      `/community/users/${encodeURIComponent(id)}/messages`,
      { method: 'POST', body: JSON.stringify({ content }) },
      true
    );
  }

  async createLumiReply(postId: string, prompt?: string) {
    return this.request<{ comment: CommunityComment }>(
      `/community/posts/${encodeURIComponent(postId)}/lumi-reply`,
      { method: 'POST', body: JSON.stringify({ prompt }) }
    );
  }

  async getCommunityComments(postId: string) {
    return this.request<{ comments: CommunityComment[] }>(
      `/community/posts/${encodeURIComponent(postId)}/comments`,
      {},
      true
    );
  }

  async createCommunityComment(postId: string, content: string, parentId?: string) {
    return this.request<{ comment: CommunityComment }>(
      `/community/posts/${encodeURIComponent(postId)}/comments`,
      { method: 'POST', body: JSON.stringify({ content, parentId }) },
      true
    );
  }

  async voteCommunityComment(id: string, vote: -1 | 0 | 1) {
    return this.request<{ score: number; userVote: -1 | 0 | 1 }>(
      `/community/comments/${encodeURIComponent(id)}/vote`,
      { method: 'POST', body: JSON.stringify({ vote }) },
      true
    );
  }

  async togglePinCommunityComment(id: string) {
    return this.request<{ comment: CommunityComment }>(
      `/community/comments/${encodeURIComponent(id)}/pin`,
      { method: 'POST' },
      true
    );
  }

  async updateCommunityComment(id: string, content: string) {
    return this.request<{ comment: CommunityComment }>(
      `/community/comments/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify({ content }) },
      true
    );
  }

  async deleteCommunityComment(id: string) {
    return this.request<{ deleted: boolean }>(
      `/community/comments/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
      true
    );
  }

  async toggleFollowCommunityUser(userId: string) {
    return this.request<{ following: boolean }>(
      `/community/users/${encodeURIComponent(userId)}/follow`,
      { method: 'POST' },
      true
    );
  }

  async getCommunityUserProfile(userId: string) {
    return this.request<{ user: CommunityProfile; isFollowing?: boolean }>(
      `/community/users/${encodeURIComponent(userId)}`,
      {},
      true
    );
  }

  async getCommunityUserProjects(userId: string) {
    return this.request<{ projects: UserProject[] }>(
      `/community/users/${encodeURIComponent(userId)}/projects`,
      {},
      true
    );
  }

  async getDirectMessages(userId: string) {
    return this.request<{
      messages: Array<{
        id: string;
        senderId: string;
        recipientId: string;
        content: string;
        createdAt: string;
      }>;
      otherUser: CommunityUser;
    }>(`/community/users/${encodeURIComponent(userId)}/messages`, {}, true);
  }

  // ─── Generic HTTP helpers ───────────────────────────────────────────
  async get<T>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'GET' }, true);
  }

  async post<T>(endpoint: string, body: unknown) {
    return this.request<T>(endpoint, { method: 'POST', body: JSON.stringify(body) }, true);
  }

  async patch<T>(endpoint: string, body: unknown) {
    return this.request<T>(endpoint, { method: 'PATCH', body: JSON.stringify(body) }, true);
  }

  async delete<T>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'DELETE' }, true);
  }

  // ─── Security & 2FA API Methods ─────────────────────────────────────
  async changePassword(input: { currentPassword: string; newPassword: string; confirmPassword: string }) {
    const res = await this.request<{ success: boolean; message?: string }>(
      '/auth/change-password',
      { method: 'POST', body: JSON.stringify(input) },
      true
    );
    return {
      success: res.success,
      message: res.data?.message,
      error: res.error,
    };
  }

  async request2FAOtp(input: { type: 'email' | 'mobile'; target: string }) {
    const res = await this.request<{
      success: boolean;
      message?: string;
      maskedTarget?: string;
      deliveryMode?: 'resend' | 'preview';
      otpPreview?: string;
    }>('/auth/2fa/request-otp', { method: 'POST', body: JSON.stringify(input) }, true);
    return {
      success: res.success,
      message: res.data?.message,
      maskedTarget: res.data?.maskedTarget,
      deliveryMode: res.data?.deliveryMode,
      otpPreview: res.data?.otpPreview,
      error: res.error,
    };
  }

  async verify2FAOtp(input: { otp: string }) {
    const res = await this.request<{
      success: boolean;
      message?: string;
      twoFactor?: { enabled: boolean; type: 'email' | 'mobile'; target: string };
    }>('/auth/2fa/verify-otp', { method: 'POST', body: JSON.stringify(input) }, true);
    return {
      success: res.success,
      message: res.data?.message,
      twoFactor: res.data?.twoFactor,
      error: res.error,
    };
  }

  async disable2FA(input: { currentPassword: string }) {
    const res = await this.request<{ success: boolean; message?: string }>(
      '/auth/2fa/disable',
      { method: 'POST', body: JSON.stringify(input) },
      true
    );
    return {
      success: res.success,
      message: res.data?.message,
      error: res.error,
    };
  }

  async getSecurityStatus() {
    const res = await this.request<{
      success: boolean;
      hasPassword: boolean;
      twoFactor: { enabled: boolean; type?: 'email' | 'mobile' | null; target?: string | null };
    }>('/auth/security/status', { method: 'GET' }, true);
    return {
      success: res.success,
      hasPassword: res.data?.hasPassword ?? true,
      twoFactor: res.data?.twoFactor ?? { enabled: false },
      error: res.error,
    };
  }
}

export const apiClient = new ApiClient();
export default apiClient;
